import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";

import { useAuth } from "../auth/AuthContext";
import { canPlayAsAccount } from "../auth/accountMode";
import { api, ApiError, type OrderPaymentInstructions, type PublicRegistrationDivision, type PublicRegistrationPayload, type PublicRegistrationPlayerProfile, type PublicRegistrationSubmitResponse, type PublicWhoPlays, type RegistrationFormField } from "../lib/api";
import { enabledRegistrationFields, fieldSection, isRegistrationFieldRequired } from "../lib/registrationFields";
import {
  accountEmailOf,
  acceptCurpNotice,
  ageOnDate,
  buildSubmitInput,
  CHILD_MODE_PLAYER_LABELS,
  childModeMissingGuardian,
  childProfileNames,
  confirmSummaryText,
  curpNotice,
  curpNoticeBlocks,
  curpPatchOnUpload,
  curpPatchRevert,
  curpPatchUseAll,
  curpPatchUseDate,
  curpPatchUseName,
  curpUploadFromParse,
  dismissSimilarNotice,
  fillGuardianFromAccount,
  guardianBoxFields,
  guardianNameMatchesPlayer,
  initialRegistrationForm,
  keepGuardianBox,
  ownNameSignature,
  playerFullName,
  resetForAnotherPlayer,
  shouldPreloadInChildMode,
  skippedInChildMode,
  typedOf,
  whoPlaysConfig,
  withApplied,
  type CurpPatch,
  type CurpUpload,
  type RegistrationFormState,
  type SessionAccount,
} from "../lib/publicRegistrationMode";
import type { CurpRegistered } from "../lib/curpClassifier";
import CurpCheckNotice from "../components/publicRegistration/CurpCheckNotice";
import FamilyDoneCard from "../components/publicRegistration/FamilyDoneCard";
import FileUploadControl from "../components/publicRegistration/FileUploadControl";
import GuardianBox, { type GuardianKey } from "../components/publicRegistration/GuardianBox";
import PlayerCurpStep from "../components/publicRegistration/PlayerCurpStep";
import RegistrationConfirmCard from "../components/publicRegistration/RegistrationConfirmCard";
import WhoPlaysChoice from "../components/publicRegistration/WhoPlaysChoice";
import { publicVenueFactValue } from "../lib/tournamentVenues";
import { safeSponsorLink, submittedWhatsappGroups, whatsappGroupButtonLabel } from "../lib/tournamentExtras";
import { formatDate } from "../lib/dateFormat";
import { splitHeroTitle } from "../lib/heroTitle";
import { heroFormatLabel, publicBallFactValue, publicCostFactValue } from "../lib/convocatoriaFacts";
import { ruleAttachments } from "../lib/ruleAttachments";
import { formatMoney } from "../lib/money";
import { useSignedLinkRefresh } from "../lib/useSignedLinkRefresh";
import { emailMatchesMasked } from "../lib/passwordRecovery";
import {
  buildDiagnostic,
  classifySubmitError,
  failureMessage,
  flushDiagnosticQueue,
  generateFolio,
  InvalidResponseError,
  reportDiagnostic,
  withTimeout,
  type DiagnosticStage,
} from "../lib/registrationDiagnostics";
import {
  categoryDisabledReason,
  categoryEligibilityError,
  categoryHasOwnCost,
  effectiveCategoryCost,
  priceSelectedCategories,
  profileHasEligibleAdditionalCategory,
  pruneIneligibleSelections,
} from "../lib/registrationCategories";
import {
  divisionBirthYearLabel,
  shortDateEs,
  yearFromTournamentDate,
} from "../lib/tournamentDivisions";

type RegistrationForm = RegistrationFormState;

const TORNEOS123_DARK_LOGO_SRC = `${import.meta.env.BASE_URL}torneos123/logo-torneos123-header-on-dark.png`;


function WhatsAppIcon() {
  return (
    <svg className="public-registration-whatsapp-button__icon" viewBox="0 0 32 32" aria-hidden="true" focusable="false">
      <path fill="currentColor" d="M16.03 3.2C9.06 3.2 3.4 8.78 3.4 15.65c0 2.2.6 4.36 1.73 6.24L3.3 28.8l7.1-1.83a12.74 12.74 0 0 0 5.62 1.3h.01c6.96 0 12.63-5.58 12.63-12.45S22.99 3.2 16.03 3.2Zm0 22.98h-.01a10.57 10.57 0 0 1-5.37-1.46l-.39-.23-4.21 1.09 1.12-4.02-.26-.41a10.2 10.2 0 0 1-1.6-5.5c0-5.71 4.82-10.35 10.73-10.35 2.87 0 5.56 1.08 7.59 3.04a10.16 10.16 0 0 1 3.14 7.48c0 5.71-4.82 10.36-10.73 10.36Zm5.88-7.76c-.32-.16-1.9-.92-2.2-1.02-.29-.11-.51-.16-.73.16-.21.31-.83 1.02-1.02 1.23-.19.21-.38.24-.7.08-.32-.16-1.36-.49-2.6-1.57-.96-.84-1.61-1.87-1.8-2.18-.19-.32-.02-.49.14-.65.15-.14.32-.37.49-.55.16-.18.21-.31.32-.52.11-.21.05-.39-.03-.55-.08-.16-.73-1.72-1-2.36-.26-.62-.53-.54-.73-.55h-.62c-.21 0-.55.08-.84.39-.29.31-1.1 1.05-1.1 2.57 0 1.52 1.13 2.99 1.29 3.2.16.21 2.23 3.35 5.4 4.69.76.32 1.35.52 1.81.66.76.24 1.45.21 2 .13.61-.09 1.9-.76 2.17-1.49.27-.73.27-1.36.19-1.49-.08-.13-.29-.21-.61-.37Z" />
    </svg>
  );
}

function resolvePublicAssetUrl(path: string | null | undefined): string | null {
  if (!path) return null;
  if (/^https?:\/\//i.test(path)) return path;
  const normalized = path.startsWith("/") ? path : `/${path}`;
  if (normalized.startsWith("/tenis/")) return normalized;
  return `${import.meta.env.BASE_URL.replace(/\/$/, "")}${normalized}`;
}

// Al teclear el nombre, el aviso de la CURP espera a que la familia deje de
// escribir: con cada letra («L», «Le», «Leo»…) el aviso rojo se prendía y
// se apagaba, y el lector de pantalla lo volvía a anunciar.
const CURP_NOTICE_TYPING_DELAY_MS = 600;
const CURP_NOTICE_ID = "public-registration-curp-check";

const MAX_DOCUMENT_SIZE_BYTES = 8 * 1024 * 1024;
const MAX_DOCUMENT_SIZE_LABEL = "8 MB";

const documentAccept = {
  photo: "image/jpeg,image/png,image/webp",
  pdf: "application/pdf",
  receipt: "application/pdf,image/jpeg,image/png,image/webp",
};

const initialForm: RegistrationForm = initialRegistrationForm;

const convocatoriaLabels: Array<[string, string]> = [
  ["objective", "Objetivo"],
  ["categories", "Categorías"],
  ["modality", "Modalidad"],
  ["surface", "Superficie"],
  ["registrationInstructions", "Inscripciones"],
  ["contacts", "Contactos"],
  ["paymentDetails", "Datos de pago"],
  ["prizes", "Premios"],
  ["officialsCost", "Árbitros / atajadores"],
  ["signatures", "Firmas"],
];

const rulesLabels: Array<[string, string]> = [
  ["competitionFormat", "Formato de competencia"],
  ["rules", "Reglas"],
  ["dressCode", "Vestimenta"],
];

function valueOrDash(value: unknown) {
  return typeof value === "string" && value.trim() ? value : "Por definir";
}

function Field({ label, children, className = "" }: { label: ReactNode; children: ReactNode; className?: string }) {
  return (
    <label className={`public-registration-field ${className}`.trim()}>
      <span>{label}</span>
      {children}
    </label>
  );
}

// Una preinscripción "viva" es la que todavía tiene algo que hacer en el paso
// 4: subir comprobante (cobro manual) o pagar en línea (orden Mercado Pago
// pendiente). Antes solo contaba la manual y la orden MP quedaba como "Ya
// inscrito" sin botón de pago.
/** Intento de envío: qué perfil (null = perfil nuevo) y qué categorías, y
 * qué había ya inscrito ANTES de enviar. Revisión #577: «Reintentar» solo
 * confirma con ESE perfil y ESAS categorías (antes otro hijo en otra
 * categoría contaba como confirmación). */
export interface SubmitAttempt {
  profileId: number | null;
  divisionIds: number[];
  profileIdsBefore: Set<number>;
  pairsBefore: Set<string>;
}

export function snapshotAttempt(
  profiles: PublicRegistrationPlayerProfile[],
  profileId: number | null,
  divisionIds: number[],
): SubmitAttempt {
  const pairsBefore = new Set<string>();
  for (const profile of profiles) {
    for (const divisionId of profile.registered_division_ids ?? []) pairsBefore.add(`${profile.id}:${divisionId}`);
  }
  return { profileId, divisionIds: [...divisionIds], profileIdsBefore: new Set(profiles.map((p) => p.id)), pairsBefore };
}

/** El perfil del intento ya tiene TODAS las categorías intentadas como
 * inscripciones nuevas (que no estaban antes de enviar). */
export function profileSavedForAttempt(
  profiles: PublicRegistrationPlayerProfile[],
  attempt: SubmitAttempt,
): PublicRegistrationPlayerProfile | null {
  if (attempt.divisionIds.length === 0) return null;
  const candidates = profiles.filter((profile) =>
    attempt.profileId !== null ? profile.id === attempt.profileId : !attempt.profileIdsBefore.has(profile.id),
  );
  // Un perfil nuevo solo cuenta si es el único nuevo (si no, es ambiguo).
  if (attempt.profileId === null && candidates.length !== 1) return null;
  return (
    candidates.find((profile) =>
      attempt.divisionIds.every(
        (divisionId) =>
          (profile.registered_division_ids ?? []).includes(divisionId) &&
          !attempt.pairsBefore.has(`${profile.id}:${divisionId}`),
      ),
    ) ?? null
  );
}

function pendingNeedsAction(
  pending: PublicRegistrationPlayerProfile["pending_registration"] | null | undefined,
): pending is NonNullable<PublicRegistrationPlayerProfile["pending_registration"]> {
  return Boolean(pending?.needs_payment_receipt || pending?.needs_online_payment);
}

// Perfil al que apunta un link de pago (?perfil=&orden=). La orden manda: es
// la del hijo cuyo «Pagar ahora» se tocó. Si perfil y orden no son del mismo
// jugador, o no son de esta cuenta, el link no elige a nadie (se ignora).
// ?paso=pago que no lleva a ningún pago pendiente (Adela 2026-10-24): nunca
// se queda en el paso 4 de un alta nueva con el formulario vacío.
export const PAYMENT_LINK_MISS_MESSAGE =
  "No encontramos un pago pendiente para este jugador; elige al jugador o termina su alta.";

function paymentLinkProfile(
  profiles: PublicRegistrationPlayerProfile[],
  profileId: number | null,
  orderToken: string | null,
): PublicRegistrationPlayerProfile | null {
  if (orderToken) {
    // Perfil y orden solo valen como PAR: orden desconocida, ajena o de otro
    // hijo → se ignoran los dos (revisión de #578).
    const owner = profiles.find((profile) => profile.pending_registration?.order?.public_token === orderToken) ?? null;
    return owner && (profileId === null || owner.id === profileId) ? owner : null;
  }
  if (profileId === null) return null;
  return profiles.find((profile) => profile.id === profileId) ?? null;
}

function profileToRegistrationForm(profile: PublicRegistrationPlayerProfile, current: RegistrationForm): RegistrationForm {
  return {
    ...current,
    player_profile_id: profile.id,
    first_name: profile.first_name,
    last_name: profile.last_name,
    maternal_last_name: profile.maternal_last_name,
    phone: profile.phone,
    email: profile.email,
    birthdate: profile.birthdate,
    gender: profile.gender,
    nationality_code: profile.nationality_code || "MEX",
    state_province: profile.state_province,
    city: profile.city,
    club_name: profile.club_name,
    national_federation_id: profile.national_federation_id,
    wtn_tennis_id: profile.wtn_tennis_id,
    dominant_hand: profile.dominant_hand,
    backhand_type: profile.backhand_type,
    guardian_name: profile.guardian_name,
    guardian_phone: profile.guardian_phone,
    guardian_relationship: profile.guardian_relationship,
    guardian_email: profile.guardian_email,
    coach_name: profile.coach_name,
    coach_phone: profile.coach_phone,
    coach_email: profile.coach_email,
  };
}

interface PublicSponsor {
  src: string;
  name: string | null;
  link: string | null;
}

/** Banners de patrocinador (Eduardo 2026-10-01: varios por torneo). Arriba
 * va el primero; abajo, apilados, los demás. Con link, la imagen abre la
 * página del patrocinador. */
function PublicAdBannerSlot({
  position,
  banners,
  tournamentName,
}: {
  position: "superior" | "inferior";
  banners: PublicSponsor[];
  tournamentName: string;
}) {
  if (banners.length === 0) return null;
  return (
    <aside
      className={`public-registration-ad-slot public-registration-ad-slot--${position} is-filled${banners.length > 1 ? " is-stack" : ""}`}
      aria-label={banners.length > 1 ? `Patrocinadores (${position})` : `Banner publicitario ${position}`}
    >
      {banners.map((banner, index) => {
        const image = <img src={banner.src} alt={banner.name ? `Patrocinador: ${banner.name}` : `Patrocinador de ${tournamentName}`} />;
        return banner.link ? (
          <a key={`${banner.src}-${index}`} href={banner.link} target="_blank" rel="noopener noreferrer sponsored">
            {image}
          </a>
        ) : (
          <span key={`${banner.src}-${index}`} className="public-registration-ad-slot__item">{image}</span>
        );
      })}
    </aside>
  );
}

/** Cuentas de cobro F1: datos bancarios congelados de la orden del jugador. */
function OrderBankDetails({ info }: { info: OrderPaymentInstructions | null | undefined }) {
  if (!info) return null;
  return (
    <div className="public-registration-bank-details order-bank-details" data-testid="order-bank-details" style={{ display: "flex", flexDirection: "column", gap: 4, minWidth: 0, overflowWrap: "anywhere" }}>
      <span style={{ fontWeight: 800 }}>Cuenta para tu pago</span>
      {info.bank_name ? <strong>{info.bank_name}</strong> : null}
      {info.beneficiary_name ? <p>Beneficiario: {info.beneficiary_name}</p> : null}
      {info.account_number ? <p>Cuenta: {info.account_number}</p> : null}
      {info.clabe ? <p>CLABE: {info.clabe}</p> : null}
      {info.payment_concept ? <p><strong>Concepto:</strong> {info.payment_concept}</p> : null}
      {info.fallback ? <p className="muted">Datos actuales del torneo.</p> : null}
    </div>
  );
}

export default function PublicTournamentRegistrationPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // ?preview=payment: vista previa del admin (no reanuda nada).
  // ?paso=pago: link real de "sube tu comprobante" (bienvenida, recordatorio,
  // estado de la orden): abre el paso 4 y, sin sesión, pide iniciar sesión.
  const location = useLocation();
  // «Inscribir a …» desde Torneos públicos del jugador: llega con el perfil
  // activo ya elegido (estado de navegación, nunca en la URL).
  const requestedProfileId = (() => {
    const state = location.state as { playerProfileId?: unknown } | null;
    return typeof state?.playerProfileId === "number" ? state.playerProfileId : null;
  })();
  const paymentPreview = searchParams.get("preview") === "payment";
  const paymentDeepLink = searchParams.get("paso") === "pago";
  // «Pagar ahora» de un hijo (Adela 2026-10-23): el link dice QUÉ jugador y
  // QUÉ orden. Solo se usan si son de la cuenta que inició sesión.
  const linkProfileId = paymentDeepLink ? Number(searchParams.get("perfil")) || null : null;
  const linkOrderToken = paymentDeepLink ? searchParams.get("orden")?.trim() || null : null;
  const startAtPayment = paymentPreview || paymentDeepLink;
  const { status: authStatus, token, user, logout } = useAuth();
  const [payload, setPayload] = useState<PublicRegistrationPayload | null>(null);
  const [form, setForm] = useState<RegistrationForm>(initialForm);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [fileInputKey, setFileInputKey] = useState(0);
  const [submittedName, setSubmittedName] = useState<string | null>(null);
  const [submittedRegistration, setSubmittedRegistration] = useState<PublicRegistrationSubmitResponse["data"] | null>(null);
  const [checkoutBusy, setCheckoutBusy] = useState(false);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);
  const [pendingRegistration, setPendingRegistration] = useState<PublicRegistrationPlayerProfile["pending_registration"] | null>(null);
  const [pendingReceiptFile, setPendingReceiptFile] = useState<File | null>(null);
  // Contexto del link (torneo, perfil, orden, paso, cuenta). Sube cada vez
  // que cambia: una subida o un checkout que empezó con el hijo A y termina
  // ya en el hijo B se ignora (revisión #583).
  const linkContextRef = useRef(0);
  const [uploadingPendingReceipt, setUploadingPendingReceipt] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Intento que no se pudo confirmar (con folio): ofrece «Reintentar» solo
  // después de revisar si en realidad sí quedó guardado (Eduardo 2026-10-23).
  const [submitFailure, setSubmitFailure] = useState<{ folio: string } | null>(null);
  const [checkingSaved, setCheckingSaved] = useState(false);
  const submitAttemptRef = useRef<SubmitAttempt | null>(null);
  const submitFormRef = useRef<HTMLFormElement>(null);
  const [showAccountRecovery, setShowAccountRecovery] = useState(false);
  // Detalle del 409 "ya existe un jugador…" (caso Eduardo Zaga 2026-09-14):
  // si la cuenta existe y nunca entró, ofrecemos mandar el correo para crear
  // su contraseña en un clic, en vez de mandarlo a un login sin contraseña.
  const [recoveryDetails, setRecoveryDetails] = useState<{
    has_account?: boolean;
    never_logged_in?: boolean;
    can_email_reset?: boolean;
    masked_email?: string | null;
    display_name?: string | null;
  } | null>(null);
  const [recoveryEmailState, setRecoveryEmailState] = useState<"idle" | "sending" | "sent" | "error">("idle");

  async function sendRecoveryEmail() {
    const email = accountEmailOf(form, whoPlays);
    if (!email) return;
    setRecoveryEmailState("sending");
    try {
      await api.auth.passwordResetRequest(email);
      setRecoveryEmailState("sent");
    } catch {
      setRecoveryEmailState("error");
    }
  }
  const [familyProfiles, setFamilyProfiles] = useState<PublicRegistrationPlayerProfile[]>([]);
  const [profileAutofillStatus, setProfileAutofillStatus] = useState<"idle" | "loading" | "loaded" | "missing" | "error">("idle");
  const [message, setMessage] = useState<string | null>(null);
  const [activeStep, setActiveStep] = useState(startAtPayment ? 4 : 1);
  const [maxStepVisited, setMaxStepVisited] = useState(startAtPayment ? 4 : 1);
  const [rulesAccepted, setRulesAccepted] = useState(startAtPayment);
  const [receiptUnderReview, setReceiptUnderReview] = useState(false);
  // «Pago recibido de <hijo>» del link de pago: también con inscripciones
  // cerradas (revisión #583).
  const [paidNotice, setPaidNotice] = useState<string | null>(null);
  // Comprobante ya subido y en revisión: la orden a la que se le puede
  // reemplazar el comprobante (Eduardo 2026-09-14: subió uno equivocado y la
  // pantalla no dejaba cambiarlo).
  const [reviewRegistration, setReviewRegistration] = useState<PublicRegistrationPlayerProfile["pending_registration"] | null>(null);
  const [feeCategory, setFeeCategory] = useState("general");
  const [curpParsing, setCurpParsing] = useState(false);
  // Lecturas de CURP (revisión #583): `curpGenerationRef` sube cada vez que
  // cambia el jugador que se llena (elegir, «Agregar», otro hijo, otro
  // perfil), aunque el id siga en null; `curpRequestRef` identifica la
  // lectura más reciente. Una respuesta vieja no llena, no muestra error y
  // no apaga el «Leyendo CURP…» de la lectura nueva.
  const curpGenerationRef = useRef(0);
  const curpRequestRef = useRef(0);
  const [curpParseMessage, setCurpParseMessage] = useState<string | null>(null);
  // CURP del jugador en revisión (Eduardo 2026-10-01): se compara EN VIVO con
  // lo escrito; si parece de otra persona no se toma fecha ni género.
  const [curpUpload, setCurpUpload] = useState<CurpUpload | null>(null);
  const curpInputRef = useRef<HTMLInputElement | null>(null);
  // «¿Quién va a jugar?»: null = lo que propone el servidor.
  const [whoPlaysChoice, setWhoPlaysChoice] = useState<PublicWhoPlays | null>(null);
  // Titular de la cuenta con sesión: llena «Tus datos» en modo hijo.
  const [sessionAccount, setSessionAccount] = useState<SessionAccount | null>(null);
  // Tarjeta «Vas a inscribir a …» antes del pago.
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [submittedWhoPlays, setSubmittedWhoPlays] = useState<PublicWhoPlays | null>(null);
  const [submittedAccountEmail, setSubmittedAccountEmail] = useState("");
  // Mientras se teclea el nombre, el aviso de la CURP se calcula con lo que
  // había al empezar a escribir (null = en vivo).
  const [noticeTyped, setNoticeTyped] = useState<CurpRegistered | null>(null);
  // «El nombre está bien, continuar» de «Ese es tu nombre» (firma).
  const [ownNameAccepted, setOwnNameAccepted] = useState<string | null>(null);
  // Con sesión y un solo perfil: se precarga cuando ya se sabe el modo (en
  // «Mi hijo o hija» no se precarga al papá en la caja del jugador).
  const [autoProfile, setAutoProfile] = useState<PublicRegistrationPlayerProfile | null>(null);
  const feedbackRef = useRef<HTMLDivElement | null>(null);
  const wizardRef = useRef<HTMLDivElement | null>(null);
  const saveDataButtonRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    let mounted = true;
    async function load() {
      if (!slug) return;
      setLoading(true);
      setError(null);
      try {
        const response = await api.publicRegistrations.get(slug);
        if (!mounted) return;
        setPayload(response.data);
      } catch (err) {
        if (!mounted) return;
        setError(err instanceof ApiError ? err.message : "No se pudo cargar la inscripción.");
      } finally {
        if (mounted) setLoading(false);
      }
    }
    void load();
    return () => { mounted = false; };
  }, [slug]);

  // Contexto de trabajo (revisión #583): torneo, jugador elegido, orden en
  // pantalla, link de pago, cuenta y paso. Cualquier cambio —link, selector
  // familiar, «Agregar nuevo», «Volver a alta», cambio de paso— invalida las
  // subidas/checkouts/lecturas de CURP en curso y limpia lo que era del
  // contexto anterior (archivo elegido, subida, checkout).
  const shownOrderId = pendingRegistration?.order?.id ?? reviewRegistration?.order?.id ?? null;
  const workContextKey = [
    slug ?? "",
    form.player_profile_id ?? "",
    shownOrderId ?? "",
    paymentDeepLink ? linkProfileId ?? "" : "",
    paymentDeepLink ? linkOrderToken ?? "" : "",
    user?.id ?? "",
    activeStep,
  ].join("|");
  useEffect(() => {
    linkContextRef.current += 1;
    setPendingReceiptFile(null);
    setUploadingPendingReceipt(false);
    setCheckoutBusy(false);
    setCheckoutError(null);
  }, [workContextKey]);

  // Cambio del jugador efectivo por fuera del selector: navegación («Inscribir
  // a …» con location.state), link de pago, cuenta que carga o perfil que
  // queda elegido. Nueva generación de CURP y se apaga «Leyendo CURP…».
  useEffect(() => {
    curpGenerationRef.current += 1;
    setCurpParsing(false);
  }, [form.player_profile_id, requestedProfileId, location.key, slug, user?.id, linkProfileId, linkOrderToken]);

  // Resultado de una subida o alta del link anterior: se borra al cambiar de
  // torneo, perfil, orden o paso (no al iniciar sesión tras inscribir).
  useEffect(() => {
    setSubmittedName(null);
    setSubmittedRegistration(null);
  }, [slug, linkProfileId, linkOrderToken, paymentDeepLink]);

  useEffect(() => {
    let mounted = true;
    // Revisión #583: todo lo que sale del link (perfil, orden, paso, cuenta)
    // se limpia al cambiar cualquiera de ellos, también navegando dentro de
    // la app sin recargar. Las respuestas viejas se descartan (`mounted`).
    linkContextRef.current += 1;
    setPaidNotice(null);
    setPendingRegistration(null);
    setReviewRegistration(null);
    setReceiptUnderReview(false);
    setMessage(null);
    // Archivo elegido, resultado de subida y checkout son del link anterior.
    setPendingReceiptFile(null);
    setUploadingPendingReceipt(false);
    setCheckoutBusy(false);
    setCheckoutError(null);
    setError(null);
    async function loadPlayerProfile() {
      // Cuenta familiar = la que opera algún perfil (propio o con acceso), no
      // el rol: la mamá con acceso al perfil del hijo también entra aquí.
      if (!slug || authStatus !== "authenticated" || !token || !user || !canPlayAsAccount(user)) {
        if (mounted && authStatus !== "loading") {
          setProfileAutofillStatus("idle");
          setFamilyProfiles([]);
          setSessionAccount(null);
          if (authStatus === "authenticated" && paymentDeepLink && !paymentPreview) leavePaymentStep();
        }
        return;
      }
      setProfileAutofillStatus("loading");
      try {
        const response = await api.publicRegistrations.me(slug, token);
        if (!mounted) return;
        const profiles = response.data.player_profiles?.length ? response.data.player_profiles : [response.data.player_profile];
        setFamilyProfiles(profiles);
        setSessionAccount(response.data.account ?? null);
        const requestedProfile =
          (requestedProfileId !== null ? profiles.find((profile) => profile.id === requestedProfileId) ?? null : null) ??
          paymentLinkProfile(profiles, linkProfileId, linkOrderToken);
        // Si llega un perfil elegido («Inscribir a …»), solo cuenta SU
        // preinscripción: la de otro familiar no secuestra el flujo. Un link
        // de pago con perfil/orden que no forman un par de esta cuenta
        // (revisión #583: perfil A + orden pagada de B) no elige a nadie.
        const linkMismatch = paymentDeepLink && (linkProfileId !== null || linkOrderToken !== null) && !requestedProfile;
        const pendingSource = requestedProfile ? [requestedProfile] : profiles;
        // Sin par válido sigue el respaldo de #578 (primer pago pendiente, con
        // su propia cuenta), pero NUNCA el estado «pagado»/«sin orden» de otro hijo.
        const noticeSource = linkMismatch ? [] : pendingSource;
        const pendingProfile = pendingSource.find((profile) => pendingNeedsAction(profile.pending_registration)) ?? null;
        const pending = pendingProfile?.pending_registration ?? null;
        setPendingRegistration(pending);
        const reviewProfile = pendingSource.find((profile) => Boolean(profile.pending_registration?.receipt_under_review)) ?? null;
        // La orden en revisión solo se muestra si se abre la pantalla de
        // pago y no hay otra pendiente: así el jugador elegido y la orden
        // mostrada siempre son el mismo (Cuentas de cobro F1).
        const showReview = reviewProfile !== null && !pending && paymentDeepLink;
        const review = showReview ? reviewProfile.pending_registration ?? null : null;
        const underReview = review !== null;
        setReceiptUnderReview(underReview);
        setReviewRegistration(review);
        const orderProfile = pending ? pendingProfile : showReview ? reviewProfile : null;
        if (orderProfile) {
          setForm((current) => profileToRegistrationForm(orderProfile, current));
        }
        const firstAvailable = profiles.find((profile) => !profile.is_registered) ?? profiles[0];
        const otherPending =
          requestedProfile !== null &&
          profiles.some((profile) => profile.id !== requestedProfile.id && pendingNeedsAction(profile.pending_registration));
        if (!pending && underReview && paymentDeepLink) {
          setRulesAccepted(true);
          setMessage("Ya recibimos tu comprobante. El organizador lo está revisando; aquí puedes reemplazarlo si te equivocaste.");
          if (!paymentPreview) {
            setActiveStep(4);
            setMaxStepVisited((current) => Math.max(current, 4));
          }
        } else if (pending) {
          setRulesAccepted(true);
          setMessage("Tienes una preinscripción guardada. Termina el pago para enviar tu inscripción.");
          if (!paymentPreview) {
            setActiveStep(4);
            setMaxStepVisited((current) => Math.max(current, 4));
          }
        } else if (requestedProfile) {
          const name = requestedProfile.display_name || `${requestedProfile.first_name} ${requestedProfile.last_name}`.trim();
          setForm((current) => profileToRegistrationForm(requestedProfile, current));
          setMessage(
            (requestedProfile.is_registered
              ? `${name} ya está inscrito; puedes inscribirlo en otra categoría.`
              : `Perfil seleccionado: ${name}. Revisa sus datos y continúa.`) +
              (otherPending ? " Otro jugador de tu familia tiene un pago pendiente en este torneo." : ""),
          );
          if (!paymentPreview) {
            setActiveStep(3);
            setMaxStepVisited((current) => Math.max(current, 3));
          }
        } else if (profiles.length === 1 && firstAvailable && !firstAvailable.is_registered) {
          // Se precarga cuando ya se sabe el modo (efecto de abajo).
          setAutoProfile(firstAvailable);
          if (!paymentPreview) {
            setActiveStep(3);
            setMaxStepVisited((current) => Math.max(current, 3));
          }
        } else {
          setForm((current) => ({ ...current, player_profile_id: null }));
          setMessage("Sesión iniciada: elige a qué jugador quieres inscribir o agrega un nuevo perfil familiar.");
          if (!paymentPreview) {
            setActiveStep(3);
            setMaxStepVisited((current) => Math.max(current, 3));
          }
        }
        if (paymentDeepLink && !paymentPreview && !pending && !underReview) {
          // El link de pago no resolvió una orden pendiente: se queda en el
          // paso 3 (elegir jugador) y dice por qué. Si el jugador ya está
          // inscrito pero sin orden, el pago lo genera el organizador (no
          // hay ruta pública que cree órdenes para una inscripción hecha).
          // El paso 4 ya no está «visitado»: «Pago» del navegador vuelve a
          // pedir un alta válida (revisión #583).
          setMaxStepVisited(3);
          const nameOf = (profile: PublicRegistrationPlayerProfile) =>
            profile.display_name || `${profile.first_name} ${profile.last_name}`.trim();
          // Orden YA pagada (Adela 2026-10-24: orden pagada e inscripción
          // aún «preinscrita»): se dice que el pago llegó y no se enseña ni
          // el alta ni la subida de comprobante.
          const paidProfile = noticeSource.find((profile) => profile.pending_registration?.order?.status === "paid");
          const withoutOrder = noticeSource.find(
            (profile) => profile.pending_registration && !profile.pending_registration.order,
          );
          if (paidProfile) {
            const confirmed = paidProfile.pending_registration?.participant_status === "active";
            const paidText = `Pago recibido de ${nameOf(paidProfile)} · ${
              confirmed ? "su inscripción está confirmada." : "su inscripción está en revisión del organizador."
            } No necesitas subir nada más.`;
            setMessage(paidText);
            setPaidNotice(paidText);
            if (!paymentPreview) {
              setActiveStep(1);
              setMaxStepVisited(1);
            }
          } else {
            setMessage(
              withoutOrder
                ? `${nameOf(withoutOrder)} ya está inscrito pero todavía no tiene un pago generado. Pide al organizador que genere su pago; cuando lo haga, aquí podrás subir tu comprobante.`
                : PAYMENT_LINK_MISS_MESSAGE,
            );
          }
        }
        setProfileAutofillStatus("loaded");
      } catch (err) {
        if (!mounted) return;
        setProfileAutofillStatus(err instanceof ApiError && err.status === 404 ? "missing" : "error");
        if (paymentDeepLink && !paymentPreview) leavePaymentStep();
      }
    }
    // Sin perfiles que leer: empieza el alta desde el inicio en vez de
    // enseñar el paso 4 con el formulario vacío.
    function leavePaymentStep() {
      setRulesAccepted(false);
      setActiveStep(1);
      setMaxStepVisited(1);
      setMessage(PAYMENT_LINK_MISS_MESSAGE);
    }
    void loadPlayerProfile();
    return () => { mounted = false; };
  }, [authStatus, linkOrderToken, linkProfileId, paymentDeepLink, paymentPreview, requestedProfileId, slug, token, user]);

  // «Ver comprobante actual» abre una liga firmada que vence en una hora. Si
  // la familia deja esta pantalla abierta, se pide solo esa liga otra vez
  // antes de que venza (sin tocar el formulario ni los mensajes).
  const reviewParticipantId = reviewRegistration?.participant_id ?? null;
  // Orden ya existente del jugador (pendiente, en revisión o recién creada).
  const existingOrder = pendingRegistration?.order ?? reviewRegistration?.order ?? submittedRegistration?.order ?? null;
  const refreshReviewReceiptLink = useCallback(async () => {
    if (!slug || !token || reviewParticipantId === null) return;
    const response = await api.publicRegistrations.me(slug, token);
    const profiles = response.data.player_profiles?.length ? response.data.player_profiles : [response.data.player_profile];
    const fresh = profiles
      .map((profile) => profile.pending_registration)
      .find((pending) => pending?.participant_id === reviewParticipantId);
    const freshUrl = fresh?.payment_receipt_url;
    if (!freshUrl) return;
    setReviewRegistration((current) =>
      current && current.participant_id === reviewParticipantId ? { ...current, payment_receipt_url: freshUrl } : current,
    );
  }, [reviewParticipantId, slug, token]);
  useSignedLinkRefresh([reviewRegistration?.payment_receipt_url], refreshReviewReceiptLink);

  const convocatoria = payload?.convocatoria ?? {};
  const loginNextPath = `/inscripcion/${slug ?? ""}${
    paymentDeepLink
      ? `?paso=pago${linkProfileId ? `&perfil=${linkProfileId}` : ""}${linkOrderToken ? `&orden=${encodeURIComponent(linkOrderToken)}` : ""}`
      : ""
  }`;
  const loginPath = `/login?next=${encodeURIComponent(loginNextPath)}`;
  const recoveryUserPath = `/login?recuperar=usuario&next=${encodeURIComponent(loginNextPath)}`;
  const recoveryPasswordPath = "/recuperar";
  const isAuthenticatedPlayer = authStatus === "authenticated" && Boolean(user && canPlayAsAccount(user));
  const isAuthenticatedNonPlayer = authStatus === "authenticated" && !(user && canPlayAsAccount(user));

  async function goToPlayerLogin() {
    setError(null);
    if (isAuthenticatedNonPlayer) {
      await logout();
    }
    navigate(loginPath);
  }
  const facts = useMemo(() => {
    if (!payload) return [];
    // Multi-sede (2ª ronda #156): convocatoria.venue solo manda cuando es un
    // override manual REAL (difiere de la sede principal auto-copiada); sin
    // override y con sedes múltiples, el dato "Sede" las lista TODAS.
    const sedeValue = publicVenueFactValue({
      convocatoriaVenue: convocatoria.venue,
      primaryVenueName: payload.tournament.venue_name,
      venues: payload.tournament.venues,
    });
    return [
      ["Torneo", payload.tournament.name],
      ["Sede", valueOrDash(sedeValue)],
      ["Fechas", `${formatDate(payload.tournament.start_date, "Por definir")} – ${formatDate(payload.tournament.end_date, "Por definir")}`],
      ["Cierre", formatDate(payload.tournament.registration_deadline, "Por definir")],
      // Pelota y costo son del Paso 1 (Datos): la copia de la convocatoria
      // puede estar vieja (Eduardo 2026-09-10: "Head Marathon" en Datos y el
      // link decía el placeholder).
      ["Costo", valueOrDash(publicCostFactValue({ registrationCost: payload.tournament.registration_cost, convocatoriaCost: convocatoria.cost }))],
      ["Pelota", valueOrDash(publicBallFactValue({ officialBall: payload.tournament.official_ball, convocatoriaBalls: convocatoria.balls }))],
    ];
  }, [payload, convocatoria]);
  const selectedDivisions = useMemo<PublicRegistrationDivision[]>(
    () => (payload?.divisions ?? []).filter((division) => form.division_ids?.includes(division.id)),
    [payload?.divisions, form.division_ids],
  );
  // Dobles por CATEGORÍA: los campos de pareja (doubles_only) solo aplican si
  // ALGUNA categoría marcada es de dobles (o el torneo entero lo es y no
  // maneja categorías). Cambiar la selección los muestra/oculta en vivo.
  const doublesRegistrationContext = useMemo(() => {
    const divisions = payload?.divisions ?? [];
    if (divisions.length === 0) return payload?.tournament.play_mode === "doubles";
    return selectedDivisions.some((division) => division.play_mode === "doubles");
  }, [payload?.divisions, payload?.tournament.play_mode, selectedDivisions]);

  const isDoublesOnlyField = useCallback(
    (field: RegistrationFormField) => Boolean(field.doubles_only) || field.key === "partner_name" || field.key === "partner_contact",
    [],
  );
  const activeRegistrationFields = useMemo(
    () =>
      enabledRegistrationFields(payload?.registration_form ?? payload?.registration_settings.registration_form_schema)
        .filter((field) => !isDoublesOnlyField(field) || doublesRegistrationContext),
    [payload?.registration_form, payload?.registration_settings.registration_form_schema, doublesRegistrationContext, isDoublesOnlyField],
  );
  const hasGenderField = activeRegistrationFields.some((field) => field.key === "gender");
  // Sin campo de género en el form: se infiere solo si TODAS las categorías
  // marcadas comparten la misma restricción varonil/femenil.
  const selectedDivisionGenders = new Set(
    selectedDivisions
      .map((division) => division.gender)
      .filter((gender): gender is "male" | "female" => gender === "male" || gender === "female"),
  );
  const inferredDivisionGender = !hasGenderField && selectedDivisionGenders.size === 1
    ? [...selectedDivisionGenders][0]
    : "";
  const submitGender = form.gender || inferredDivisionGender;
  const registrationSettings = payload?.registration_settings;
  const organizerLogoUrl = resolvePublicAssetUrl(payload?.tournament.organizer_logo_url ?? registrationSettings?.credential_logo_url ?? null);
  const tournamentHeroImageUrl = resolvePublicAssetUrl(payload?.tournament.logo_url ?? null);
  // Eduardo 2026-09-15: clic en el póster → verlo en grande y descargarlo.
  const [posterOpen, setPosterOpen] = useState(false);
  useEffect(() => {
    if (!posterOpen) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setPosterOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [posterOpen]);
  const heroTitle = useMemo(() => splitHeroTitle(payload?.tournament.name ?? ""), [payload?.tournament.name]);
  const heroOrganizerName = (payload?.tournament.organizer_name ?? "").trim() || null;
  const heroMeta = useMemo(() => {
    if (!payload) return [] as string[];
    const items: string[] = [];
    const format = heroFormatLabel({
      tournamentType: payload.tournament.tournament_type,
      divisionFormats: payload.divisions.filter((division) => division.is_active !== false).map((division) => division.format_type),
    });
    if (format) items.push(format);
    const venue = (payload.tournament.venue_name ?? "").trim();
    if (venue) items.push(venue);
    if (payload.tournament.start_date) items.push(`Inicia ${formatDate(payload.tournament.start_date)}`);
    return items;
  }, [payload]);
  const sponsorBannerUrl = resolvePublicAssetUrl(payload?.tournament.sponsor_banner_url ?? null);
  // Todos los patrocinadores (Eduardo 2026-10-01); un servidor viejo solo
  // manda el banner único de siempre.
  const publicSponsors: PublicSponsor[] = payload?.tournament.sponsor_banners
    ? payload.tournament.sponsor_banners.flatMap((banner) => {
        const src = resolvePublicAssetUrl(banner.image_url);
        return src ? [{ src, name: banner.name, link: safeSponsorLink(banner.link) }] : [];
      })
    : sponsorBannerUrl
      ? [{ src: sponsorBannerUrl, name: null, link: null }]
      : [];
  const topSponsors = publicSponsors.slice(0, 1);
  const bottomSponsors = publicSponsors.slice(1);
  const paymentProvider = registrationSettings?.provider ?? "manual";
  // Solo Mercado Pago está activo para pago en línea del jugador; Stripe
  // Connect sigue dormido y este flujo debe comportarse como manual.
  const usesOnlinePayment = paymentProvider === "mercadopago_marketplace";
  const tournamentYear = yearFromTournamentDate(payload?.tournament.start_date);
  const selectedDivisionEligibilityErrors = selectedDivisions
    .map((division) => categoryEligibilityError(division, form.birthdate, submitGender, tournamentYear))
    .filter((eligibilityError): eligibilityError is string => eligibilityError !== null);
  const registrationOpen = payload?.registration_open !== false;
  const openDivisionsCount = typeof payload?.open_divisions_count === "number" ? payload.open_divisions_count : null;
  // Categorías donde el perfil familiar elegido YA está inscrito: se
  // deshabilitan en los checkboxes para que solo agregue categorías nuevas.
  const selectedProfileRegisteredDivisionIds = useMemo(() => {
    const profile = familyProfiles.find((item) => item.id === form.player_profile_id);
    return new Set(profile?.registered_division_ids ?? []);
  }, [familyProfiles, form.player_profile_id]);
  const feeOptions = payload?.fee_options ?? [];
  // Sin useEffect de reset: si el payload cambió y el tipo elegido ya no
  // existe, se degrada a general en el MISMO render (display y submit).
  const activeFeeCategory = feeOptions.some((option) => option.category === feeCategory)
    ? feeCategory
    : "general";
  const selectedFeeOption = feeOptions.find((option) => option.category === activeFeeCategory) ?? null;
  // Costo por categoría adicional: categorías donde el perfil elegido ya
  // tiene inscripción vigente a precio completo (solo con sesión; sin sesión
  // el servidor aplica la regla igual y la orden muestra el monto final).
  const selectedProfileBaseRegistrations = useMemo(() => {
    const profile = familyProfiles.find((item) => item.id === form.player_profile_id);
    return profile?.active_base_registrations ?? [];
  }, [familyProfiles, form.player_profile_id]);
  const additionalCategoryFeeAmount = payload?.registration_settings.additional_category_fee_amount ?? null;
  const pricedCategories = priceSelectedCategories(
    payload?.divisions ?? [],
    form.division_ids ?? [],
    payload?.registration_settings.entry_fee_amount,
    selectedFeeOption,
    additionalCategoryFeeAmount,
    selectedProfileBaseRegistrations.length > 0,
  );
  const pricedById = new Map(pricedCategories.map((row) => [row.id, row]));
  const orderTotal = pricedCategories.reduce((total, row) => total + row.amount, 0);
  const additionalPriced = pricedCategories.filter((row) => row.isAdditional);
  const isMinorRegistration = isMinorByBirthdate(form.birthdate);
  const hasField = useCallback((key: string) => activeRegistrationFields.some((field) => field.key === key), [activeRegistrationFields]);
  const fieldByKey = useMemo(
    () => new Map(activeRegistrationFields.map((field) => [field.key, field])),
    [activeRegistrationFields],
  );
  const usesMexTourCupLayout = activeRegistrationFields.some((field) => field.key.startsWith("mex_"));
  // «¿Quién va a jugar?» (Eduardo 2026-10-01): solo si el servidor lo pide
  // (categorías infantiles o datos del tutor). Mex Tour Cup sigue en «Yo».
  const whoPlaysSettings = whoPlaysConfig(payload);
  const askWhoPlays = whoPlaysSettings.ask && !usesMexTourCupLayout;
  const whoPlays: PublicWhoPlays = askWhoPlays ? whoPlaysChoice ?? whoPlaysSettings.default : "self";
  const childMode = whoPlays === "child";
  // El 409 puede venir por celular, FMT o cuenta familiar: la cuenta puede
  // tener OTRO correo que el escrito. Pedir el enlace con el escrito no
  // mandaría nada (Alex Zayat 2026-10-03, «no me llega»), así que solo se
  // ofrece el botón si el correo escrito es el de la cuenta.
  const recoveryEmailIsAccount = emailMatchesMasked(accountEmailOf(form, whoPlays), recoveryDetails?.masked_email);
  const publicFieldLabel = useCallback((field: RegistrationFormField) => {
    if (field.key === "partner_name") return childMode ? "¿Quién es la pareja del jugador?" : "¿Quién es tu pareja?";
    if (field.key === "partner_contact") return childMode ? "Teléfono o correo de su pareja" : "Teléfono o correo de tu pareja";
    return field.label;
  }, [childMode]);
  const fieldLabel = useCallback((key: string, fallback: string) => {
    const field = fieldByKey.get(key);
    return field ? publicFieldLabel(field) : fallback;
  }, [fieldByKey, publicFieldLabel]);
  const playerNameFieldLabel = useCallback((key: string, fallback: string) => (
    <>
      {fieldLabel(key, fallback)} <small className="public-registration-field__hint">(del jugador)</small>
    </>
  ), [fieldLabel]);
  // Campos personalizados por sección: los del tutor van con el tutor y, en
  // modo hijo, los de «Otros» van después de las categorías.
  const customFieldsBySection = useMemo(() => {
    const custom = activeRegistrationFields.filter((field) => field.kind === "custom");
    return {
      player: custom.filter((field) => fieldSection(field) === "player"),
      guardian: custom.filter((field) => fieldSection(field) === "guardian"),
      other: custom.filter((field) => fieldSection(field) === "other"),
    };
  }, [activeRegistrationFields]);
  const guardianBox = guardianBoxFields(activeRegistrationFields);
  // Lo último escrito, para decidir qué llena la CURP al terminar de leerla.
  const formRef = useRef(form);
  formRef.current = form;
  const curpContext = { whoPlays, guardianName: form.guardian_name, startDate: payload?.tournament.start_date ?? null };
  // En vivo: es lo que detiene «Guardar datos».
  const activeCurpNotice = curpNotice(curpUpload, typedOf(form), curpContext);
  // Lo que se VE: mientras se teclea el nombre, el aviso de antes de teclear.
  const shownCurpNotice = noticeTyped ? curpNotice(curpUpload, noticeTyped, curpContext) : activeCurpNotice;
  const typedNameKey = [form.first_name, form.last_name, form.maternal_last_name].join("|");
  useEffect(() => {
    if (!noticeTyped) return;
    const timer = window.setTimeout(() => setNoticeTyped(null), CURP_NOTICE_TYPING_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [noticeTyped, typedNameKey]);
  // Nombres de los hijos de la cuenta con sesión: una cuenta vieja con el
  // nombre de un hijo no pone a ese hijo en «Tu nombre» (el perfil adulto
  // del propio papá no cuenta).
  const familyNames = useMemo(() => childProfileNames(familyProfiles, tournamentYear), [familyProfiles, tournamentYear]);
  // Sin CURP: el papá o la mamá escribió su propio nombre como jugador.
  const showOwnNameWarning =
    childMode &&
    !curpUpload &&
    guardianNameMatchesPlayer(form.guardian_name, form.first_name, form.last_name, form.maternal_last_name) &&
    ownNameAccepted !== ownNameSignature(form);
  const confirmText = confirmSummaryText({
    whoPlays,
    name: playerFullName(form),
    age: hasField("birthdate") ? ageOnDate(form.birthdate, payload?.tournament.start_date) : null,
    categories: selectedDivisions.map((division) => division.name),
  });

  // Con sesión, en modo hijo «Tus datos» arranca con los de la cuenta y el
  // correo es siempre el de la cuenta (la caja no se edita).
  useEffect(() => {
    if (!childMode || !sessionAccount) return;
    setForm((current) => fillGuardianFromAccount(current, sessionAccount, familyNames));
  }, [childMode, sessionAccount, familyNames, form.guardian_email]);

  // Con sesión y un solo perfil: se precarga, salvo en «Mi hijo o hija»
  // cuando ese perfil es del papá (adulto o titular sin fecha).
  useEffect(() => {
    if (!autoProfile || !payload) return;
    setAutoProfile(null);
    if (childMode && !shouldPreloadInChildMode(autoProfile, sessionAccount, tournamentYear)) {
      setMessage("Sesión iniciada: llena los datos de quien va a jugar.");
      return;
    }
    setForm((current) => {
      const next = profileToRegistrationForm(autoProfile, current);
      return childMode ? keepGuardianBox(current, next, sessionAccount, familyNames) : next;
    });
    setMessage("Sesión iniciada: cargamos tu perfil guardado. También puedes agregar otro jugador familiar.");
  }, [autoProfile, payload, childMode, sessionAccount, tournamentYear, familyNames]);

  // Recalcular elegibilidad al cambiar fecha de nacimiento, género o perfil
  // familiar: las categorías marcadas que dejaron de ser válidas se sueltan
  // solas (el checkbox queda deshabilitado con su motivo visible). Incluye
  // las categorías donde el perfil elegido YA está inscrito (B5): sin la
  // poda quedaban checked+disabled — imposibles de desmarcar — y el submit
  // terminaba en 409 division_already_registered.
  useEffect(() => {
    const divisions = payload?.divisions ?? [];
    const selectedIds = form.division_ids ?? [];
    if (divisions.length === 0 || selectedIds.length === 0) return;
    const pruned = pruneIneligibleSelections(
      divisions,
      selectedIds,
      form.birthdate,
      submitGender,
      tournamentYear,
      selectedProfileRegisteredDivisionIds,
    );
    if (pruned !== selectedIds) {
      setForm((current) => ({ ...current, division_ids: pruned }));
    }
  }, [payload?.divisions, form.division_ids, form.birthdate, submitGender, tournamentYear, selectedProfileRegisteredDivisionIds]);

  // Los datos del tutor de un adulto en «Yo» NO se borran de la pantalla:
  // tocar «Yo» por error y regresar a «Mi hijo o hija» dejaba vacía la caja
  // 1. Al enviar, `buildSubmitInput` ya no los manda si no aplican.

  function showError(messageText: string) {
    setError(messageText);
    window.setTimeout(() => {
      feedbackRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      feedbackRef.current?.focus();
    }, 0);
  }

  function update<K extends keyof RegistrationForm>(key: K, value: RegistrationForm[K]) {
    setShowAccountRecovery(false);
    setForm((current) => {
      const next = { ...current, [key]: value };
      // Eduardo 2026-09-23 (torneo 59): el formulario venía precargado con un
      // perfil de la familia y el papá escribe el nombre de OTRO hijo sin
      // pulsar "Agregar nuevo jugador familiar". Otro primer nombre = otra
      // persona: se suelta el perfil precargado para crear uno nuevo y no
      // renombrar al hermano (el backend aplica la misma regla).
      if (key === "first_name" && current.player_profile_id !== null) {
        const prefilled = familyProfiles.find((item) => item.id === current.player_profile_id);
        const firstToken = (text: string | null | undefined) =>
          (text ?? "").trim().toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").split(/\s+/)[0] ?? "";
        const typed = String(value ?? "");
        const otherName = Boolean(prefilled) && firstToken(typed) !== "" && firstToken(typed) !== firstToken(prefilled?.first_name);
        if (otherName) {
          next.player_profile_id = null;
          setMessage("Estás inscribiendo a otra persona: se creará un perfil familiar nuevo ligado a tu cuenta.");
        }
      }
      return next;
    });
  }

  // Nombre del jugador tecleado: el aviso de la CURP espera a que termine.
  function updateTypedName(key: "first_name" | "last_name" | "maternal_last_name", value: string) {
    if (curpUpload) setNoticeTyped((snapshot) => snapshot ?? typedOf(formRef.current));
    update(key, value);
  }

  function toggleDivisionSelection(divisionId: number) {
    setShowAccountRecovery(false);
    setForm((current) => {
      const selected = current.division_ids ?? [];
      return {
        ...current,
        division_ids: selected.includes(divisionId)
          ? selected.filter((id) => id !== divisionId)
          : [...selected, divisionId],
      };
    });
  }

  function updateCustomAnswer(key: string, value: string | boolean) {
    setShowAccountRecovery(false);
    setForm((current) => ({
      ...current,
      custom_answers: { ...current.custom_answers, [key]: value },
    }));
  }

  // B3 (gate QUINTO #129): ¿este perfil ya inscrito puede agregar al menos
  // una categoría más? categorías del torneo − ya inscritas − no elegibles
  // por edad/género de ESE perfil (y sin cupo). Decide tanto el estilo de la
  // tarjeta (is-disabled) como el bloqueo del click: un perfil que SÍ puede
  // agregar no debe verse (ni comportarse) como prohibido. Cubre también el
  // torneo sin categorías: sin divisiones no hay "otra categoría" que
  // agregar.
  function profileCanAddAnotherCategory(profile: PublicRegistrationPlayerProfile): boolean {
    if (!profile.is_registered) return true;
    return profileHasEligibleAdditionalCategory(
      payload?.divisions ?? [],
      profile.registered_division_ids ?? [],
      profile.birthdate,
      profile.gender,
      tournamentYear,
    );
  }

  /** Todo el estado de la orden (pago pendiente, comprobante en revisión,
   * datos bancarios congelados) sale del jugador ELEGIDO: al cambiar de
   * familiar no se arrastra la orden de otro (Cuentas de cobro F1). */
  function applyPlayerOrderState(profile: PublicRegistrationPlayerProfile | null) {
    const pendingReg = profile?.pending_registration ?? null;
    const needsAction: boolean = pendingNeedsAction(pendingReg);
    const inReview = Boolean(pendingReg?.receipt_under_review) && !needsAction;
    setPendingRegistration(needsAction ? pendingReg : null);
    setReviewRegistration(inReview ? pendingReg : null);
    setReceiptUnderReview(inReview);
    setPendingReceiptFile(null);
    setSubmittedRegistration(null);
  }

  function selectFamilyProfile(profile: PublicRegistrationPlayerProfile) {
    const name = profile.display_name || profile.first_name;
    const inReview = Boolean(profile.pending_registration?.receipt_under_review);
    const needsAction: boolean = pendingNeedsAction(profile.pending_registration);
    const hasOrderToShow = needsAction || inReview;
    // Primero se valida: si la selección no procede, NO cambia nada (ni el
    // jugador elegido ni su orden/cuenta).
    if (!hasOrderToShow && !profileCanAddAnotherCategory(profile)) {
      showError(`${name} ya está inscrito en este torneo y no tiene otra categoría disponible para agregar.`);
      return;
    }
    // Aceptada: jugador elegido y estado de su orden cambian juntos (mismo
    // manejador, un solo render).
    startNewCurpGeneration();
    applyPlayerOrderState(profile);
    setConfirmOpen(false);
    setForm((current) => {
      const next = profileToRegistrationForm(profile, current);
      // En modo hijo, «Tus datos» (la cuenta) no cambia al elegir otro hijo.
      return childMode ? keepGuardianBox(current, next, sessionAccount, familyNames) : next;
    });
    setError(null);
    if (needsAction) {
      setRulesAccepted(true);
      setMessage(`${name} ya tiene una preinscripción guardada. Termina el pago para enviarla.`);
      openStep(4);
      return;
    }
    if (inReview) {
      setRulesAccepted(true);
      setMessage(`${name}: ya recibimos su comprobante. El organizador lo está revisando; aquí puedes reemplazarlo.`);
      openStep(4);
      return;
    }
    setMessage(
      profile.is_registered
        ? `${name} ya está inscrito; puedes inscribirlo en otra categoría adicional.`
        : `Perfil seleccionado: ${profile.display_name || `${profile.first_name} ${profile.last_name}`}. Revisa sus datos y continúa.`,
    );
  }

  function startNewCurpGeneration() {
    curpGenerationRef.current += 1;
    setCurpParsing(false);
  }

  function addNewFamilyProfile() {
    startNewCurpGeneration();
    applyPlayerOrderState(null);
    setError(null);
    setConfirmOpen(false);
    setMessage("Nuevo perfil familiar: llena los datos del jugador que quieres inscribir. Quedará ligado a tu cuenta.");
    setForm((current) =>
      childMode
        ? resetForAnotherPlayer(current, activeRegistrationFields)
        : {
          ...initialForm,
          privacy_notice_accepted: current.privacy_notice_accepted,
          data_consent: current.data_consent,
        },
    );
    setCurpUpload(null);
    setCurpParseMessage(null);
    setFileInputKey((current) => current + 1);
  }

  function changeWhoPlays(next: PublicWhoPlays) {
    setWhoPlaysChoice(next);
    setConfirmOpen(false);
    setError(null);
  }

  function updateGuardian(key: GuardianKey, value: string) {
    update(key, value);
  }

  function updateFile(key: "photo_file" | "curp_pdf_file" | "payment_receipt_file", file: File | null) {
    if (file && file.size > MAX_DOCUMENT_SIZE_BYTES) {
      showError(`El archivo "${file.name}" excede el límite de ${MAX_DOCUMENT_SIZE_LABEL}.`);
      update(key, null);
      return;
    }
    setError(null);
    update(key, file);
  }

  function applyCurpPatch(upload: CurpUpload, patch: CurpPatch) {
    setForm((current) => ({ ...current, ...patch }));
    setCurpUpload(withApplied(upload, patch));
    setNoticeTyped(null);
  }

  // «Usar datos de la CURP» (parece de otra persona): todo lo de la CURP.
  function applyAllCurpData() {
    if (!curpUpload) return;
    applyCurpPatch(curpUpload, curpPatchUseAll(curpUpload));
    setCurpParseMessage("Datos de la CURP aplicados. Revísalos antes de seguir.");
    setError(null);
  }

  // «Usar la fecha de la CURP» (el nombre coincide, la fecha escrita no).
  function applyCurpDate() {
    if (!curpUpload) return;
    applyCurpPatch(curpUpload, curpPatchUseDate(curpUpload, form));
    setCurpParseMessage("Fecha de la CURP aplicada.");
    setError(null);
  }

  // «Subir otra CURP»: quita lo que esa CURP llenó y abre el selector.
  function uploadAnotherCurp() {
    const revert = curpPatchRevert(curpUpload, form);
    setForm((current) => ({ ...current, ...revert, curp_pdf_file: null }));
    setCurpUpload(null);
    setNoticeTyped(null);
    setCurpParseMessage(null);
    setError(null);
    const input = curpInputRef.current;
    if (input) {
      input.value = "";
      input.focus();
      input.click();
    }
  }

  // «El nombre está bien, continuar»: guarda la firma; si después cambia el
  // nombre, la fecha o el género, el aviso vuelve a abrirse.
  function acceptCurpCheck() {
    if (!curpUpload) return;
    const { upload, patch } = acceptCurpNotice(curpUpload, typedOf(form), curpContext);
    applyCurpPatch(upload, patch);
    setError(null);
  }

  function applyCurpSuggestedName() {
    if (!curpUpload) return;
    applyCurpPatch(curpUpload, curpPatchUseName(curpUpload));
    setCurpParseMessage("Datos del documento aplicados. Revisa que sean correctos antes de continuar.");
  }

  function keepTypedCurpName() {
    if (!curpUpload) return;
    setCurpUpload(dismissSimilarNotice(curpUpload, typedOf(form)));
    setNoticeTyped(null);
    setCurpParseMessage("Conservamos el nombre que escribiste.");
  }

  async function handleCurpFile(file: File | null) {
    setCurpParseMessage(null);
    setCurpUpload(null);
    setNoticeTyped(null);
    updateFile("curp_pdf_file", file);
    if (!file || !slug || file.size > MAX_DOCUMENT_SIZE_BYTES) return;
    // La CURP es del jugador que se está llenando: si se elige otro antes de
    // que termine la lectura, el resultado se ignora (revisión #583).
    // Dos candados: el turno (generación + número de lectura) y la identidad
    // del jugador destino (su id). Cualquiera que cambie invalida la lectura.
    const generation = curpGenerationRef.current;
    const request = ++curpRequestRef.current;
    const curpFor = formRef.current.player_profile_id;
    const sameCurpPlayer = () =>
      generation === curpGenerationRef.current &&
      request === curpRequestRef.current &&
      formRef.current.player_profile_id === curpFor;
    setCurpParsing(true);
    try {
      const response = await api.publicRegistrations.parseCurp(slug, file);
      if (!sameCurpPlayer()) return;
      const upload = curpUploadFromParse(response.data);
      if (!upload) {
        setCurpParseMessage(response.data.message);
        return;
      }
      // Lo que la CURP llena se decide con lo escrito AHORA: nunca fecha ni
      // género de una CURP que parece de otra persona (Eduardo 2026-10-01).
      const current = formRef.current;
      const context = { ...curpContext, guardianName: current.guardian_name };
      const patch = curpPatchOnUpload(upload, typedOf(current), context);
      applyCurpPatch(upload, patch);
      setError(null);
      const pendingNotice = curpNotice(withApplied(upload, patch), typedOf({ ...current, ...patch }), context);
      setCurpParseMessage(pendingNotice ? null : response.data.message);
    } catch (err) {
      if (!sameCurpPlayer()) return;
      setCurpUpload(null);
      setCurpParseMessage(null);
      showError(err instanceof ApiError ? err.message : "No pudimos leer automáticamente la CURP. Puedes llenar los datos manualmente.");
    } finally {
      if (sameCurpPlayer()) setCurpParsing(false);
    }
  }

  // Reemplazo del comprobante de una orden pendiente ya con comprobante: sube
  // el nuevo archivo y se queda en el paso 4 con el aviso actualizado.
  async function replacePaymentReceipt() {
    if (!slug || !token || !reviewRegistration?.order || !pendingReceiptFile) {
      showError("Selecciona el nuevo comprobante de pago.");
      return;
    }
    if (pendingReceiptFile.size > MAX_DOCUMENT_SIZE_BYTES) {
      showError(`El archivo "${pendingReceiptFile.name}" excede el límite de ${MAX_DOCUMENT_SIZE_LABEL}.`);
      return;
    }
    const context = linkContextRef.current;
    setUploadingPendingReceipt(true);
    setError(null);
    try {
      await api.publicRegistrations.uploadPaymentReceipt(slug, reviewRegistration.order.id, pendingReceiptFile, token);
      if (context !== linkContextRef.current) return;
      setPendingReceiptFile(null);
      setFileInputKey((current) => current + 1);
      setMessage("Comprobante reemplazado. El organizador revisará el nuevo archivo.");
    } catch (err) {
      if (context !== linkContextRef.current) return;
      showError(err instanceof ApiError ? err.message : "No pudimos reemplazar el comprobante. Intenta de nuevo.");
    } finally {
      if (context === linkContextRef.current) setUploadingPendingReceipt(false);
    }
  }

  async function uploadPendingPaymentReceipt() {
    if (!slug || !token || !pendingRegistration?.order || !pendingReceiptFile) {
      showError("Selecciona el comprobante de pago para completar tu preinscripción.");
      return;
    }
    if (pendingReceiptFile.size > MAX_DOCUMENT_SIZE_BYTES) {
      showError(`El archivo "${pendingReceiptFile.name}" excede el límite de ${MAX_DOCUMENT_SIZE_LABEL}.`);
      return;
    }
    const context = linkContextRef.current;
    setUploadingPendingReceipt(true);
    setError(null);
    try {
      const response = await api.publicRegistrations.uploadPaymentReceipt(slug, pendingRegistration.order.id, pendingReceiptFile, token);
      if (context !== linkContextRef.current) return;
      setPendingRegistration(null);
      setPendingReceiptFile(null);
      const participantName = response.data.order.participant?.participant_label || "Jugador";
      setSubmittedName(participantName);
      setSubmittedRegistration({
        player_profile_id: response.data.order.player_profile_id ?? 0,
        participant_id: response.data.order.participant_id,
        division_id: response.data.order.participant?.division_id ?? null,
        division: null,
        display_name: participantName,
        order: response.data.order,
        payment: {
          online_checkout_available: false,
          checkout_provider: response.data.order.provider,
          checkout_message: "Comprobante recibido. El organizador lo revisará para confirmar la inscripción.",
        },
        registration_documents: {
          payment_receipt_url: response.data.order.participant?.registration_documents?.payment_receipt_url ?? null,
        },
      });
      setMessage(null);
    } catch (err) {
      if (context !== linkContextRef.current) return;
      showError(err instanceof ApiError ? err.message : "No pudimos subir el comprobante. Intenta de nuevo.");
    } finally {
      if (context === linkContextRef.current) setUploadingPendingReceipt(false);
    }
  }

  function scrollWizardTop() {
    window.setTimeout(() => {
      wizardRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 0);
  }

  function openStep(step: number) {
    setActiveStep(step);
    setMaxStepVisited((current) => Math.max(current, step));
    scrollWizardTop();
  }

  // «Falta completar»: en modo hijo, los nombres del jugador con las
  // etiquetas de la caja 2 (un «Nombre» suelto junto a «Tu nombre» confunde).
  function missingFieldLabel(field: RegistrationFormField) {
    return (childMode && CHILD_MODE_PLAYER_LABELS[field.key]) || publicFieldLabel(field);
  }

  function configuredMissingFields(includeConsents = false) {
    // Modo hijo: primero «Tus datos» (celular y correo son los de la cuenta).
    const missing: string[] = childMode ? childModeMissingGuardian(form, activeRegistrationFields) : [];
    activeRegistrationFields.forEach((field) => {
      if (childMode && skippedInChildMode(field)) return;
      if (!isRegistrationFieldRequired(field, form, isMinorRegistration, whoPlays)) return;
      if (field.kind === "document") {
        const value = field.key === "photo" ? form.photo_file : field.key === "curp_pdf" ? form.curp_pdf_file : field.key === "payment_receipt" ? form.payment_receipt_file : null;
        if (!value) missing.push(missingFieldLabel(field));
        return;
      }
      if (field.kind === "custom") {
        const value = form.custom_answers[field.key];
        if (value === null || value === undefined || value === "" || value === false) missing.push(missingFieldLabel(field));
        return;
      }
      const value = form[field.key as keyof RegistrationForm];
      if (value === null || value === undefined || value === "") missing.push(missingFieldLabel(field));
    });
    if ((payload?.divisions?.length ?? 0) > 0 && (form.division_ids?.length ?? 0) === 0) missing.push("Categorías del torneo");
    selectedDivisionEligibilityErrors.forEach((eligibilityError) => missing.push(eligibilityError));
    if (includeConsents) {
      const effectiveRulesAccepted = rulesAccepted || activeStep >= 3 || maxStepVisited >= 3;
      if (!effectiveRulesAccepted) missing.push("Aceptar el reglamento");
      if (!form.privacy_notice_accepted) missing.push("Aceptar Aviso de Privacidad");
      if (!form.data_consent) missing.push("Aceptar uso de datos para gestionar la inscripción");
    }
    return missing;
  }

  function validatePlayerStep() {
    return configuredMissingFields(false);
  }

  // Un aviso fuerte de la CURP («Esta CURP es de…», «Esta CURP es tuya», la
  // fecha) detiene el paso hasta que la familia elige una de sus opciones; en
  // un celular puede quedar varias pantallas arriba del botón: se lleva ahí.
  function stopForCurpNotice() {
    if (!curpNoticeBlocks(activeCurpNotice)) return false;
    setNoticeTyped(null);
    setConfirmOpen(false);
    setActiveStep(3);
    setMessage(null);
    setError("Revisa el aviso de la CURP antes de seguir.");
    window.setTimeout(() => {
      const notice = document.getElementById(CURP_NOTICE_ID);
      notice?.scrollIntoView?.({ behavior: "smooth", block: "center" });
      notice?.focus();
    }, 0);
    return true;
  }

  function handleStepClick(step: number) {
    if (step === 4 && stopForCurpNotice()) return;
    if (step <= maxStepVisited) {
      openStep(step);
      return;
    }
    if (step === 2) {
      openStep(2);
      return;
    }
    if (step >= 3 && !rulesAccepted) {
      setActiveStep(2);
      setMaxStepVisited((current) => Math.max(current, 2));
      showError("Primero acepta el reglamento para continuar al alta de jugador.");
      return;
    }
    if (step === 3) {
      openStep(3);
      return;
    }
    const missing = validatePlayerStep();
    setActiveStep(3);
    setMaxStepVisited((current) => Math.max(current, 3));
    if (missing.length > 0) {
      showError(`Antes de pasar a pago falta completar: ${missing.join(", ")}.`);
      return;
    }
    // Antes del pago, siempre «Vas a inscribir a …».
    setError(null);
    setConfirmOpen(true);
  }

  function continueFromRules() {
    if (!rulesAccepted) {
      showError("Marca el checkbox de aceptación del reglamento para continuar.");
      return;
    }
    setError(null);
    setRulesAccepted(true);
    openStep(3);
  }

  function savePlayerDataAndContinue() {
    if (stopForCurpNotice()) return;
    const missing = validatePlayerStep();
    if (missing.length > 0) {
      setConfirmOpen(false);
      showError(`Antes de pasar a pago falta completar: ${missing.join(", ")}.`);
      setMessage(null);
      return;
    }
    setError(null);
    setMessage(null);
    setConfirmOpen(true);
  }

  // «Sí, es correcto» en «Vas a inscribir a …».
  function confirmPlayerData() {
    setConfirmOpen(false);
    setRulesAccepted(true);
    setMessage("Datos guardados en esta pantalla. Continúa con el pago para enviar tu inscripción.");
    openStep(4);
  }

  // «Corregir»: la tarjeta (con el foco) desaparece; el foco vuelve a
  // «Guardar datos», junto a los datos por corregir, y no al inicio.
  function correctPlayerData() {
    setConfirmOpen(false);
    window.setTimeout(() => saveDataButtonRef.current?.focus(), 0);
  }

  // Con sesión, la lista de la familia se vuelve a pedir: el hijo recién
  // inscrito aparece (y con sus categorías ya tomadas). Sin tocar el
  // formulario ni el paso.
  async function refreshFamilyProfiles() {
    if (!slug || !token || !isAuthenticatedPlayer) return;
    try {
      const response = await api.publicRegistrations.me(slug, token);
      const profiles = response.data.player_profiles?.length ? response.data.player_profiles : [response.data.player_profile];
      setFamilyProfiles(profiles);
      setSessionAccount(response.data.account ?? null);
    } catch {
      // Sin la lista nueva se sigue con la de antes; el servidor cuida los duplicados.
    }
  }

  // «Inscribir a otro jugador» (pantalla final de modo hijo): misma cuenta,
  // «Tus datos» llenos y el jugador en blanco.
  function registerAnotherPlayer() {
    startNewCurpGeneration();
    setSubmittedName(null);
    setSubmittedRegistration(null);
    setSubmittedWhoPlays(null);
    setSubmittedAccountEmail("");
    setCheckoutError(null);
    setShowAccountRecovery(false);
    setError(null);
    setMessage(null);
    setConfirmOpen(false);
    setCurpUpload(null);
    setNoticeTyped(null);
    setCurpParseMessage(null);
    setForm((current) => resetForAnotherPlayer(current, activeRegistrationFields));
    setFileInputKey((current) => current + 1);
    setRulesAccepted(true);
    openStep(3);
    // El pago del jugador anterior no deja saltar al paso 4 sin pasar por
    // «Vas a inscribir a …» con el nuevo.
    setMaxStepVisited(3);
    void refreshFamilyProfiles();
  }

  function isMinorByBirthdate(birthdate: string) {
    if (!birthdate) return false;
    const born = new Date(`${birthdate}T00:00:00`);
    if (Number.isNaN(born.getTime())) return false;
    const today = new Date();
    let age = today.getFullYear() - born.getFullYear();
    const monthDelta = today.getMonth() - born.getMonth();
    if (monthDelta < 0 || (monthDelta === 0 && today.getDate() < born.getDate())) {
      age -= 1;
    }
    return age < 18;
  }

  function validateForm() {
    return configuredMissingFields(true);
  }

  // Avisos de intentos fallidos que quedaron sin enviar (sin internet): se
  // mandan al abrir la página y cuando vuelve la conexión.
  useEffect(() => {
    const flush = () => {
      void flushDiagnosticQueue(token);
    };
    flush();
    window.addEventListener("online", flush);
    return () => window.removeEventListener("online", flush);
  }, [token]);

  async function retryAfterFailure() {
    if (!slug) return;
    if (token) {
      setCheckingSaved(true);
      try {
        const response = await api.publicRegistrations.me(slug, token);
        const profiles = response.data.player_profiles?.length ? response.data.player_profiles : [response.data.player_profile];
        const attempt = submitAttemptRef.current;
        const savedProfile = attempt ? profileSavedForAttempt(profiles, attempt) : null;
        if (savedProfile) {
          setFamilyProfiles(profiles);
          setSubmitFailure(null);
          setError(null);
          const name = savedProfile.display_name || `${savedProfile.first_name} ${savedProfile.last_name}`.trim();
          const pending = savedProfile.pending_registration ?? null;
          if (pendingNeedsAction(pending)) {
            setPendingRegistration(pending);
            setRulesAccepted(true);
            setActiveStep(4);
            setMaxStepVisited((current) => Math.max(current, 4));
            setMessage(`La inscripción de ${name} sí quedó guardada; no la envíes otra vez. Solo falta terminar el pago.`);
          } else {
            setMessage(`La inscripción de ${name} sí quedó guardada; no necesitas enviarla otra vez.`);
          }
          return;
        }
      } catch {
        // No se pudo revisar: se reintenta igual; el servidor rechaza
        // duplicados con su aviso de «ya inscrito».
      } finally {
        setCheckingSaved(false);
      }
    }
    setSubmitFailure(null);
    submitFormRef.current?.requestSubmit();
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!slug) return;
    if (stopForCurpNotice()) return;
    const missing = validateForm();
    if (missing.length > 0) {
      showError(`Falta completar: ${missing.join(", ")}.`);
      setMessage(null);
      return;
    }
    setSubmitting(true);
    setError(null);
    setSubmitFailure(null);
    setCheckoutError(null);
    setCheckoutBusy(false);
    setMessage(null);
    const folio = generateFolio();
    let stage: DiagnosticStage = "antes_de_enviar";
    submitAttemptRef.current = snapshotAttempt(familyProfiles, form.player_profile_id ?? null, form.division_ids ?? []);
    try {
      // «Mi hijo o hija»: la cuenta queda con el correo del papá o la mamá
      // (who_plays=child). «Yo»: exactamente lo de siempre.
      const input = buildSubmitInput(form, {
        whoPlays,
        sendWhoPlays: askWhoPlays,
        isMinor: isMinorRegistration,
        gender: submitGender,
        feeCategory: activeFeeCategory,
      });
      stage = "durante_envio";
      const response = await withTimeout((signal) => api.publicRegistrations.submit(slug, input, token, signal));
      stage = "al_procesar_respuesta";
      if (!response || typeof response !== "object" || !response.data || typeof response.data.display_name !== "string") {
        throw new InvalidResponseError();
      }
      setSubmittedName(response.data.display_name);
      setSubmittedRegistration(response.data);
      setSubmittedWhoPlays(whoPlays);
      setSubmittedAccountEmail(response.data.account_email || accountEmailOf(form, whoPlays));
      setShowAccountRecovery(false);
      setMessage(null);
      setConfirmOpen(false);
      setCurpParseMessage(null);
      if (whoPlays === "child") {
        // «Tus datos» se conservan para «Inscribir a otro jugador».
        setCurpUpload(null);
      } else {
        setForm(initialForm);
        setCurpUpload(null);
        setRulesAccepted(false);
        setActiveStep(1);
        setMaxStepVisited(1);
        setFileInputKey((current) => current + 1);
      }
    } catch (err) {
      const failure = classifySubmitError(err, stage);
      // Solo datos técnicos: nada del formulario sale en el aviso.
      void reportDiagnostic(
        buildDiagnostic({ folio, slug, divisionId: form.division_ids?.[0] ?? null, failure }),
        token,
      );
      if (err instanceof ApiError && err.status === 409) {
        setShowAccountRecovery(err.code === "existing_player_profile" || Array.isArray(err.details.recovery_options));
        setRecoveryDetails(err.code === "existing_player_profile" ? (err.details as typeof recoveryDetails) : null);
        setRecoveryEmailState("idle");
        setActiveStep(3);
        setMaxStepVisited((current) => Math.max(current, 3));
        showError(err.message);
      } else {
        setShowAccountRecovery(false);
        // Ya no se afirma «no se envió»: a veces SÍ llegó y solo se perdió la
        // respuesta. Se muestra un folio para revisar qué pasó (2026-10-23).
        showError(failureMessage(failure, folio, err instanceof ApiError ? err.message : null));
        if (failure.kind !== "conflicto" && failure.kind !== "validacion") {
          setSubmitFailure({ folio });
        }
      }
    } finally {
      setSubmitting(false);
    }
  }

  async function openSubmittedCheckout() {
    await openCheckoutFor(submittedRegistration?.order ?? null);
  }

  // Sirve tanto para la orden recién creada como para una preinscripción
  // guardada que vuelve al link: el jugador va directo a pagar, no a subir
  // comprobante (pedido de Eduardo 2026-09-03).
  async function openCheckoutFor(order: { public_token?: string | null; provider?: string | null } | null | undefined) {
    if (!slug || !order?.public_token) return;
    const context = linkContextRef.current;
    setCheckoutBusy(true);
    setCheckoutError(null);
    const checkoutWindow = window.open("about:blank", "_blank");
    if (checkoutWindow) checkoutWindow.opener = null;
    try {
      const response = await api.publicPayments.startCheckout(
        slug,
        order.public_token,
        order.provider || paymentProvider,
      );
      const checkoutUrl = response.data.checkout_url;
      if (context !== linkContextRef.current) {
        // Ya se cambió de jugador: no se abre el pago del anterior.
        checkoutWindow?.close();
        return;
      }
      if (checkoutUrl) {
        if (checkoutWindow) {
          checkoutWindow.location.href = checkoutUrl;
        } else {
          window.location.href = checkoutUrl;
        }
        return;
      }
      checkoutWindow?.close();
      setCheckoutError(response.data.message || "No se pudo abrir Mercado Pago para esta orden.");
    } catch (err) {
      checkoutWindow?.close();
      if (context !== linkContextRef.current) return;
      setCheckoutError(err instanceof ApiError ? err.message : "No se pudo abrir Mercado Pago. Intenta de nuevo.");
    } finally {
      if (context === linkContextRef.current) setCheckoutBusy(false);
    }
  }

  function renderCustomField(field: RegistrationFormField) {
    const value = form.custom_answers[field.key];
    const label = `${publicFieldLabel(field)}${isRegistrationFieldRequired(field, form, isMinorRegistration, whoPlays) ? " *" : ""}`;
    if (field.type === "select") {
      return (
        <Field key={field.key} label={label}>
          <select className="t123-select" value={String(value ?? "")} onChange={(event) => updateCustomAnswer(field.key, event.target.value)}>
            <option value="">Seleccionar</option>
            {(field.options ?? []).map((option) => <option key={option} value={option}>{option}</option>)}
          </select>
          {field.help_text ? <small className="form-help-text">{field.help_text}</small> : null}
        </Field>
      );
    }
    if (field.type === "checkbox") {
      return (
        <label className="form-checkbox public-registration-consent" key={field.key}>
          <input type="checkbox" checked={Boolean(value)} onChange={(event) => updateCustomAnswer(field.key, event.target.checked)} />
          <span>{label}{field.help_text ? <small className="form-help-text">{field.help_text}</small> : null}</span>
        </label>
      );
    }
    if (field.type === "textarea") {
      return <Field key={field.key} label={label}><textarea value={String(value ?? "")} onChange={(event) => updateCustomAnswer(field.key, event.target.value)} rows={3} />{field.help_text ? <small className="form-help-text">{field.help_text}</small> : null}</Field>;
    }
    return <Field key={field.key} label={label}><input type={field.type === "number" || field.type === "date" ? field.type : "text"} value={String(value ?? "")} onChange={(event) => updateCustomAnswer(field.key, event.target.value)} />{field.help_text ? <small className="form-help-text">{field.help_text}</small> : null}</Field>;
  }

  function renderCustomFieldByKey(key: string) {
    const field = fieldByKey.get(key);
    return field?.kind === "custom" ? renderCustomField(field) : null;
  }

  function renderMexTourCupPlayerStep() {
    return (
      <div className="mex-tour-registration-flow" data-registration-layout="mex-tour-cup-v2">
        <section className="mex-tour-form-card mex-tour-form-card--player">
          <div className="mex-tour-form-card__header">
            <span>01</span>
            <div>
              <h3>Datos del jugador</h3>
              <p>Primero lo básico: quién juega y dónde podemos contactarlo.</p>
            </div>
          </div>
          <div className="public-registration-form-grid mex-tour-form-grid">
            {hasField("first_name") ? <Field label={fieldLabel("first_name", "Nombre")}><input required value={form.first_name} onChange={(e) => updateTypedName("first_name", e.target.value)} /></Field> : null}
            {hasField("last_name") ? <Field label={fieldLabel("last_name", "Apellido paterno")}><input required value={form.last_name} onChange={(e) => updateTypedName("last_name", e.target.value)} /></Field> : null}
            {hasField("maternal_last_name") ? <Field label={fieldLabel("maternal_last_name", "Apellido materno")}><input value={form.maternal_last_name ?? ""} onChange={(e) => updateTypedName("maternal_last_name", e.target.value)} /></Field> : null}
            {hasField("phone") ? <Field label={fieldLabel("phone", "Celular")}><input type="tel" value={form.phone ?? ""} onChange={(e) => update("phone", e.target.value)} /></Field> : null}
            {hasField("email") ? <Field label={fieldLabel("email", "Correo")}><input required type="email" value={form.email} onChange={(e) => update("email", e.target.value)} /></Field> : null}
            {hasField("birthdate") ? <Field label={fieldLabel("birthdate", "Fecha de nacimiento")}><input type="date" value={form.birthdate ?? ""} onChange={(e) => update("birthdate", e.target.value)} /></Field> : null}
            {hasField("gender") ? <Field label={fieldLabel("gender", "Género")}><select className="t123-select" value={form.gender ?? ""} onChange={(e) => update("gender", e.target.value)}><option value="">Seleccionar</option><option value="male">Masculino</option><option value="female">Femenino</option></select></Field> : null}
            {hasField("state_province") ? <Field label={fieldLabel("state_province", "Estado")}><input value={form.state_province ?? ""} onChange={(e) => update("state_province", e.target.value)} /></Field> : null}
            {hasField("city") ? <Field label={fieldLabel("city", "Ciudad")}><input value={form.city ?? ""} onChange={(e) => update("city", e.target.value)} /></Field> : null}
            {hasField("club_name") ? <Field label={fieldLabel("club_name", "Club / Academia")}><input value={form.club_name ?? ""} onChange={(e) => update("club_name", e.target.value)} /></Field> : null}
          </div>
        </section>

        <section className="mex-tour-form-card mex-tour-form-card--category">
          <div className="mex-tour-form-card__header">
            <span>02</span>
            <div>
              <h3>Categoría y nivel</h3>
              <p>Elige dónde quieres competir. El organizador podrá validar tu nivel antes de armar cuadros.</p>
            </div>
          </div>
          <div className="public-registration-form-grid mex-tour-form-grid">
            {renderCustomFieldByKey("mex_category_requested")}
            {renderCustomFieldByKey("mex_women_fallback_category")}
            {hasField("national_federation_id") ? <Field label={fieldLabel("national_federation_id", "ID Federativo / FMT")}><input value={form.national_federation_id ?? ""} onChange={(e) => update("national_federation_id", e.target.value)} /></Field> : null}
            {hasField("wtn_tennis_id") ? <Field label={fieldLabel("wtn_tennis_id", "WTN Tennis ID")}><input value={form.wtn_tennis_id ?? ""} onChange={(e) => update("wtn_tennis_id", e.target.value)} /></Field> : null}
            {hasField("dominant_hand") ? <Field label={fieldLabel("dominant_hand", "Mano dominante")}><select className="t123-select" value={form.dominant_hand ?? ""} onChange={(e) => update("dominant_hand", e.target.value)}><option value="">Seleccionar</option><option value="right">Derecha</option><option value="left">Izquierda</option></select></Field> : null}
            {hasField("backhand_type") ? <Field label={fieldLabel("backhand_type", "Tipo de revés")}><select className="t123-select" value={form.backhand_type ?? ""} onChange={(e) => update("backhand_type", e.target.value)}><option value="">Seleccionar</option><option value="one_hand">Una mano</option><option value="two_hands">Dos manos</option></select></Field> : null}
            {renderCustomFieldByKey("mex_utr_rating")}
            {renderCustomFieldByKey("mex_amtp_ranking")}
          </div>
        </section>

        <section className="mex-tour-form-card mex-tour-form-card--availability">
          <div className="mex-tour-form-card__header">
            <span>03</span>
            <div>
              <h3>Disponibilidad</h3>
              <p>Contesta rápido cuándo puedes jugar para ayudar al organizador con horarios.</p>
            </div>
          </div>
          <div className="public-registration-form-grid mex-tour-form-grid mex-tour-form-grid--single">
            {renderCustomFieldByKey("mex_friday_availability")}
            {renderCustomFieldByKey("mex_saturday_availability")}
            {renderCustomFieldByKey("mex_sunday_availability")}
            {renderCustomFieldByKey("mex_consolation_preference")}
          </div>
        </section>

        <section className="mex-tour-form-card mex-tour-form-card--extras">
          <div className="mex-tour-form-card__header">
            <span>04</span>
            <div>
              <h3>Últimos detalles</h3>
              <p>Solo lo necesario para avisos y operación del torneo.</p>
            </div>
          </div>
          <div className="public-registration-form-grid mex-tour-form-grid">
            {renderCustomFieldByKey("mex_whatsapp_group_joined")}
            {renderCustomFieldByKey("mex_instagram_handle")}
          </div>
        </section>
      </div>
    );
  }

  // ------------------------------------------------------------ paso 3
  // Partes del alta. «Yo» conserva el orden de siempre; «Mi hijo o hija»
  // (Eduardo 2026-10-01) va: 1 · Tus datos, 2 · Datos de quien va a jugar,
  // categorías, otros datos y foto.

  function renderFamilyPicker() {
    return isAuthenticatedPlayer ? (
      <section className="public-registration-family-card" aria-labelledby="public-registration-family-title">
        <div className="public-section-heading public-section-heading--compact">
          <span>👨‍👩‍👧</span>
          <div>
            <h3 id="public-registration-family-title">{childMode ? "¿A quién vas a inscribir?" : "¿A quién quieres inscribir?"}</h3>
            {childMode ? null : <p>Selecciona un perfil familiar existente o agrega otro jugador a esta cuenta.</p>}
          </div>
        </div>
        {familyProfiles.length > 0 ? (
          <div className="public-registration-family-grid">
            {familyProfiles.map((profile) => {
              const fullName = profile.display_name || `${profile.first_name} ${profile.last_name}`.trim();
              const isSelected = form.player_profile_id === profile.id;
              // B3 (gate QUINTO #129): el estilo deshabilitado SOLO
              // cuando el perfil no puede agregar NINGUNA categoría
              // más; "ya inscrito pero con categorías elegibles" se
              // ve normal — antes parecía prohibido aunque el texto
              // dijera lo contrario.
              const canAddAnother = profileCanAddAnotherCategory(profile);
              return (
                <button
                  key={profile.id}
                  type="button"
                  className={`public-registration-family-card__profile${isSelected ? " is-selected" : ""}${profile.is_registered && !canAddAnother ? " is-disabled" : ""}`}
                  onClick={() => selectFamilyProfile(profile)}
                >
                  <strong>{fullName || `Jugador #${profile.id}`}</strong>
                  <span>{profile.birthdate ? `Nacimiento: ${formatDate(profile.birthdate)}` : "Perfil familiar"}</span>
                  {profile.is_registered ? (
                    <small>{canAddAnother ? "Ya inscrito · puede agregar otra categoría" : "Ya inscrito en este torneo"}</small>
                  ) : (
                    <small>{isSelected ? "Seleccionado" : "Usar este perfil"}</small>
                  )}
                </button>
              );
            })}
          </div>
        ) : (
          <p className="public-registration-parser-status">Esta cuenta todavía no tiene perfiles familiares. Agrega el primer jugador.</p>
        )}
        <button className="button button--secondary" type="button" onClick={addNewFamilyProfile}>
          {childMode ? "+ Agregar otro hijo" : "+ Agregar nuevo jugador familiar"}
        </button>
      </section>
    ) : null;
  }

  function renderCurpStep() {
    if (!hasField("curp_pdf")) return null;
    return (
      <PlayerCurpStep
        key={`curp-${fileInputKey}`}
        title={childMode ? "Sube su CURP y llenamos sus datos" : "Sube tu CURP y llenamos tus datos"}
        inputId={`public-registration-curp-${fileInputKey}`}
        file={form.curp_pdf_file}
        parsing={curpParsing}
        message={shownCurpNotice ? null : curpParseMessage}
        multiple={Boolean(curpUpload?.multiple)}
        onFile={(file) => { void handleCurpFile(file); }}
        inputRef={curpInputRef}
      >
        {shownCurpNotice ? (
          <CurpCheckNotice
            id={CURP_NOTICE_ID}
            notice={shownCurpNotice}
            whoPlays={whoPlays}
            onUseCurp={applyAllCurpData}
            onUseDate={applyCurpDate}
            onUploadAnother={uploadAnotherCurp}
            onAccept={acceptCurpCheck}
            onUseName={applyCurpSuggestedName}
            onKeepTyped={keepTypedCurpName}
          />
        ) : null}
      </PlayerCurpStep>
    );
  }

  function renderFeeTypeSection() {
    if (!payload) return null;
    return feeOptions.length > 1 ? (
      <section className="public-registration-documents" aria-labelledby="public-registration-fee-type-title">
        <h3 className="public-registration-documents__title" id="public-registration-fee-type-title">Tipo de inscripción</h3>
        <p>Selecciona tu tipo. El organizador validará lo que declares; el costo se ajusta según tu selección.</p>
        <div className="public-registration-division-options">
          {feeOptions.map((option) => (
            <label key={option.category} className="form-checkbox public-registration-consent public-registration-division-option">
              <input
                type="radio"
                name="public-registration-fee-category"
                checked={activeFeeCategory === option.category}
                onChange={() => setFeeCategory(option.category)}
              />
              <span>
                <strong>{option.label}</strong>
                <small className="form-help-text">Costo: {formatMoney(Number(option.amount), payload.registration_settings.currency)}</small>
              </span>
            </label>
          ))}
        </div>
        {(payload.divisions ?? []).some((division) => categoryHasOwnCost(division)) ? (
          <p className="form-help-text">Las categorías con costo propio no cambian con el tipo de inscripción.</p>
        ) : null}
      </section>
    ) : null;
  }

  function renderCategoriesSection() {
    if (!payload) return null;
    return (payload.divisions?.length ?? 0) > 0 ? (
      <section className="public-registration-documents public-registration-division-select" aria-labelledby="public-registration-division-title">
        <h3 className="public-registration-documents__title" id="public-registration-division-title">Categorías del torneo</h3>
        {openDivisionsCount !== null && openDivisionsCount < payload.divisions.length ? (
          <p className="public-registration-open-count" role="note">
            <span className="public-registration-open-count__chip">
              Inscripciones abiertas en {openDivisionsCount} {openDivisionsCount === 1 ? "categoría" : "categorías"}
            </span>
          </p>
        ) : null}
        <p>{childMode ? "Marca las categorías donde va a competir." : "Marca todas las categorías donde quieres competir."} Los costos se suman en una sola orden. El organizador podrá validarlas antes de armar los enfrentamientos.</p>
        <div className="public-registration-division-options">
          {/* Las cerradas van al final (maqueta Cierre-Categoria). */}
          {[...payload.divisions].sort((a, b) => Number(Boolean(a.is_closed)) - Number(Boolean(b.is_closed))).map((division) => {
            const disabledReason = selectedProfileRegisteredDivisionIds.has(division.id)
              ? "Ya inscrito en esta categoría"
              : categoryDisabledReason(division, form.birthdate, submitGender, tournamentYear);
            const checked = form.division_ids?.includes(division.id) ?? false;
            const birthLabel = divisionBirthYearLabel(division, tournamentYear);
            const costLabel = formatMoney(
              effectiveCategoryCost(division, payload.registration_settings.entry_fee_amount, selectedFeeOption),
              payload.registration_settings.currency,
            );
            return (
              <label
                key={division.id}
                className={`form-checkbox public-registration-consent public-registration-division-option${disabledReason ? " is-disabled" : ""}`}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  disabled={Boolean(disabledReason)}
                  onChange={() => toggleDivisionSelection(division.id)}
                />
                <span>
                  <strong>{division.name}</strong>
                  <small className="form-help-text">
                    {birthLabel ? `${birthLabel} (año ${tournamentYear}) · ` : ""}Costo: {costLabel}
                  </small>
                  {division.is_closed ? (
                    <span className="public-registration-division-state is-closed">Cerrada</span>
                  ) : division.effective_registration_deadline ? (
                    <span className="public-registration-division-state is-open">
                      Abierta · cierra el {shortDateEs(division.effective_registration_deadline)}
                    </span>
                  ) : null}
                  {/* Eduardo 2026-09-15: sin la clase de error — GlobalToasts
                      recoge todo .form-error y estos motivos (categorías que
                      el jugador ni marcó) salían como avisos flotantes. */}
                  {disabledReason ? <small className="form-help-text public-registration-division-option__reason">{disabledReason}</small> : null}
                </span>
              </label>
            );
          })}
        </div>
        {selectedDivisions.length > 0 ? (
          <div className="public-registration-payment-summary public-registration-order-summary">
            <span>Total a pagar</span>
            {selectedDivisions.map((division) => {
              const priced = pricedById.get(division.id);
              return (
                <small key={division.id}>
                  {division.name} · {formatMoney(priced?.amount ?? 0, payload.registration_settings.currency)}
                  {priced?.isAdditional ? " (categoría adicional)" : ""}
                </small>
              );
            })}
            <strong>Total: {formatMoney(orderTotal, payload.registration_settings.currency)}</strong>
          </div>
        ) : null}
        {additionalPriced.length > 0 && additionalCategoryFeeAmount !== null ? (
          <div className="alert alert--info public-registration-additional-note" role="note" data-testid="additional-category-note">
            {selectedProfileBaseRegistrations.length > 0
              ? `Ya estás inscrito en ${selectedProfileBaseRegistrations.map((row) => row.name).join(", ")}; `
              : "Te inscribes en más de una categoría; "}
            {additionalPriced.length === 1
              ? `esta categoría adicional te cuesta ${formatMoney(additionalPriced[0].amount, payload.registration_settings.currency)}.`
              : `como categorías adicionales te cuestan: ${additionalPriced
                  .map((row) => `${payload.divisions?.find((division) => division.id === row.id)?.name ?? "categoría"} ${formatMoney(row.amount, payload.registration_settings.currency)}`)
                  .join(", ")}.`}
          </div>
        ) : null}
        {doublesRegistrationContext ? (
          <div className="alert alert--info" role="note">
            <strong>{(payload?.divisions?.length ?? 0) > 0 ? "Categoría de dobles" : "Torneo de dobles"}:</strong> {childMode ? "la inscripción y el pago son individuales." : "tu inscripción y pago son individuales."}
            {childMode ? " En los datos del jugador indica quién es su pareja" : " Más abajo indica quién es tu pareja"}; cuando ambos estén inscritos, el organizador confirmará la pareja.
          </div>
        ) : null}
        {selectedDivisionEligibilityErrors.map((eligibilityError) => (
          <p className="form-error" key={eligibilityError}>{eligibilityError}</p>
        ))}
      </section>
    ) : null;
  }

  function renderSelfPlayerFields() {
    return usesMexTourCupLayout ? renderMexTourCupPlayerStep() : (
      <div className="public-registration-form-grid">
        {hasField("first_name") ? <Field label={playerNameFieldLabel("first_name", "Nombre")}><input required value={form.first_name} onChange={(e) => updateTypedName("first_name", e.target.value)} /></Field> : null}
        {hasField("last_name") ? <Field label={playerNameFieldLabel("last_name", "Apellido paterno")}><input required value={form.last_name} onChange={(e) => updateTypedName("last_name", e.target.value)} /></Field> : null}
        {hasField("maternal_last_name") ? <Field label={playerNameFieldLabel("maternal_last_name", "Apellido materno")}><input value={form.maternal_last_name ?? ""} onChange={(e) => updateTypedName("maternal_last_name", e.target.value)} /></Field> : null}
        {hasField("phone") ? <Field label={fieldLabel("phone", "Celular")}><input type="tel" value={form.phone ?? ""} onChange={(e) => update("phone", e.target.value)} /></Field> : null}
        {hasField("email") ? <Field label={fieldLabel("email", "Correo")}><input required type="email" value={form.email} onChange={(e) => update("email", e.target.value)} /></Field> : null}
        {hasField("birthdate") ? <Field label={fieldLabel("birthdate", "Fecha de nacimiento")}><input type="date" value={form.birthdate ?? ""} onChange={(e) => update("birthdate", e.target.value)} /></Field> : null}
        {hasField("gender") ? <Field label={fieldLabel("gender", "Género")}><select className="t123-select" value={form.gender ?? ""} onChange={(e) => update("gender", e.target.value)}><option value="">Seleccionar</option><option value="male">Masculino</option><option value="female">Femenino</option></select></Field> : null}
        {hasField("nationality_code") ? <Field label={fieldLabel("nationality_code", "Nacionalidad")}><input maxLength={3} value={form.nationality_code ?? "MEX"} onChange={(e) => update("nationality_code", e.target.value)} /></Field> : null}
        {hasField("state_province") ? <Field label={fieldLabel("state_province", "Estado")}><input value={form.state_province ?? ""} onChange={(e) => update("state_province", e.target.value)} /></Field> : null}
        {hasField("city") ? <Field label={fieldLabel("city", "Ciudad")}><input value={form.city ?? ""} onChange={(e) => update("city", e.target.value)} /></Field> : null}
        {hasField("club_name") ? <Field label={fieldLabel("club_name", "Club / Academia")}><input value={form.club_name ?? ""} onChange={(e) => update("club_name", e.target.value)} /></Field> : null}
        {hasField("national_federation_id") ? <Field label={fieldLabel("national_federation_id", "ID Federativo / FMT")}><input value={form.national_federation_id ?? ""} onChange={(e) => update("national_federation_id", e.target.value)} /></Field> : null}
        {hasField("wtn_tennis_id") ? <Field label={fieldLabel("wtn_tennis_id", "WTN Tennis ID")}><input value={form.wtn_tennis_id ?? ""} onChange={(e) => update("wtn_tennis_id", e.target.value)} /></Field> : null}
        {hasField("dominant_hand") ? <Field label={fieldLabel("dominant_hand", "Mano dominante")}><select className="t123-select" value={form.dominant_hand ?? ""} onChange={(e) => update("dominant_hand", e.target.value)}><option value="">Seleccionar</option><option value="right">Derecha</option><option value="left">Izquierda</option></select></Field> : null}
        {hasField("backhand_type") ? <Field label={fieldLabel("backhand_type", "Tipo de revés")}><select className="t123-select" value={form.backhand_type ?? ""} onChange={(e) => update("backhand_type", e.target.value)}><option value="">Seleccionar</option><option value="one_hand">Una mano</option><option value="two_hands">Dos manos</option></select></Field> : null}
        {activeRegistrationFields.filter((field) => field.kind === "custom" && fieldSection(field) !== "guardian").map(renderCustomField)}
      </div>
    );
  }

  // Datos del jugador en modo hijo: sin celular ni correo (son los de la
  // cuenta) y con etiquetas que dicen de quién son.
  function renderChildPlayerFields() {
    return (
      <div className="public-registration-form-grid">
        {hasField("first_name") ? <Field className="public-registration-field--wide" label="Nombre del jugador"><input required autoComplete="off" value={form.first_name} onChange={(e) => updateTypedName("first_name", e.target.value)} /></Field> : null}
        {hasField("last_name") || hasField("maternal_last_name") ? (
          <fieldset className="public-registration-name-group">
            <legend>Apellidos del jugador</legend>
            <div className="public-registration-form-grid">
              {hasField("last_name") ? <Field label="Primer apellido"><input required autoComplete="off" value={form.last_name} onChange={(e) => updateTypedName("last_name", e.target.value)} /></Field> : null}
              {hasField("maternal_last_name") ? <Field label="Segundo apellido"><input autoComplete="off" value={form.maternal_last_name ?? ""} onChange={(e) => updateTypedName("maternal_last_name", e.target.value)} /></Field> : null}
            </div>
          </fieldset>
        ) : null}
        {showOwnNameWarning ? (
          <div className="public-registration-own-name-warning" role="status">
            <p>Ese es tu nombre. Aquí van los datos de quien va a jugar.</p>
            {/* Papá e hijo con el mismo nombre: la familia puede seguir. */}
            <button className="public-registration-curp-check__accept" type="button" onClick={() => setOwnNameAccepted(ownNameSignature(form))}>
              El nombre está bien, continuar
            </button>
          </div>
        ) : null}
        {hasField("birthdate") ? <Field label={fieldLabel("birthdate", "Fecha de nacimiento")}><input type="date" value={form.birthdate ?? ""} onChange={(e) => update("birthdate", e.target.value)} /></Field> : null}
        {hasField("gender") ? <Field label={fieldLabel("gender", "Género")}><select className="t123-select" value={form.gender ?? ""} onChange={(e) => update("gender", e.target.value)}><option value="">Seleccionar</option><option value="male">Masculino</option><option value="female">Femenino</option></select></Field> : null}
        {hasField("nationality_code") ? <Field label={fieldLabel("nationality_code", "Nacionalidad")}><input maxLength={3} value={form.nationality_code ?? "MEX"} onChange={(e) => update("nationality_code", e.target.value)} /></Field> : null}
        {hasField("state_province") ? <Field label={fieldLabel("state_province", "Estado")}><input value={form.state_province ?? ""} onChange={(e) => update("state_province", e.target.value)} /></Field> : null}
        {hasField("city") ? <Field label={fieldLabel("city", "Ciudad")}><input value={form.city ?? ""} onChange={(e) => update("city", e.target.value)} /></Field> : null}
        {hasField("club_name") ? <Field label={fieldLabel("club_name", "Club / Academia")}><input value={form.club_name ?? ""} onChange={(e) => update("club_name", e.target.value)} /></Field> : null}
        {hasField("national_federation_id") ? <Field label={fieldLabel("national_federation_id", "ID Federativo / FMT")}><input value={form.national_federation_id ?? ""} onChange={(e) => update("national_federation_id", e.target.value)} /></Field> : null}
        {hasField("wtn_tennis_id") ? <Field label={fieldLabel("wtn_tennis_id", "WTN Tennis ID")}><input value={form.wtn_tennis_id ?? ""} onChange={(e) => update("wtn_tennis_id", e.target.value)} /></Field> : null}
        {hasField("dominant_hand") ? <Field label={fieldLabel("dominant_hand", "Mano dominante")}><select className="t123-select" value={form.dominant_hand ?? ""} onChange={(e) => update("dominant_hand", e.target.value)}><option value="">Seleccionar</option><option value="right">Derecha</option><option value="left">Izquierda</option></select></Field> : null}
        {hasField("backhand_type") ? <Field label={fieldLabel("backhand_type", "Tipo de revés")}><select className="t123-select" value={form.backhand_type ?? ""} onChange={(e) => update("backhand_type", e.target.value)}><option value="">Seleccionar</option><option value="one_hand">Una mano</option><option value="two_hands">Dos manos</option></select></Field> : null}
        {customFieldsBySection.player.map(renderCustomField)}
      </div>
    );
  }

  function renderTutorLegalFieldset() {
    return isMinorRegistration && (["guardian_name", "guardian_phone", "guardian_relationship", "guardian_email"].some(hasField) || customFieldsBySection.guardian.length > 0) ? (
      <fieldset className="public-registration-guardian">
        <legend>Tutor legal</legend>
        <p>Requerido únicamente para jugadores menores de 18 años al momento del registro.</p>
        <div className="public-registration-form-grid">
          {hasField("guardian_name") ? <Field label="Nombre del tutor"><input value={form.guardian_name ?? ""} onChange={(e) => update("guardian_name", e.target.value)} /></Field> : null}
          {hasField("guardian_phone") ? <Field label="Teléfono del tutor"><input type="tel" value={form.guardian_phone ?? ""} onChange={(e) => update("guardian_phone", e.target.value)} /></Field> : null}
          {hasField("guardian_relationship") ? <Field label="Parentesco"><input value={form.guardian_relationship ?? ""} onChange={(e) => update("guardian_relationship", e.target.value)} /></Field> : null}
          {hasField("guardian_email") ? <Field label="Email del tutor"><input type="email" value={form.guardian_email ?? ""} onChange={(e) => update("guardian_email", e.target.value)} /></Field> : null}
          {customFieldsBySection.guardian.map(renderCustomField)}
        </div>
      </fieldset>
    ) : null;
  }

  // Modo hijo: los campos personalizados de «Otros», después de categorías.
  function renderOtherCustomSection() {
    if (customFieldsBySection.other.length === 0) return null;
    return (
      <section className="public-registration-documents" aria-labelledby="public-registration-other-title">
        <h3 className="public-registration-documents__title" id="public-registration-other-title">Otros datos</h3>
        <div className="public-registration-form-grid">
          {customFieldsBySection.other.map(renderCustomField)}
        </div>
      </section>
    );
  }

  function renderCoachFieldset() {
    return ["coach_name", "coach_phone", "coach_email"].some(hasField) ? (
    <fieldset className="public-registration-guardian">
      {/* Sin "(opcional)" fijo: si el organizador marcó estos campos
          como obligatorios en su formulario, la validación los exige
          y la etiqueta no debe contradecirla. */}
      <legend>Profesor</legend>
      <div className="public-registration-form-grid">
        {hasField("coach_name") ? <Field label="Nombre del profesor"><input value={form.coach_name ?? ""} onChange={(e) => update("coach_name", e.target.value)} /></Field> : null}
        {hasField("coach_phone") ? <Field label="Teléfono del profesor"><input type="tel" value={form.coach_phone ?? ""} onChange={(e) => update("coach_phone", e.target.value)} /></Field> : null}
        {hasField("coach_email") ? <Field label="Email del profesor"><input type="email" value={form.coach_email ?? ""} onChange={(e) => update("coach_email", e.target.value)} /></Field> : null}
      </div>
    </fieldset>
    ) : null;
  }

  function renderPhotoFieldset() {
    return hasField("photo") ? (
      <fieldset className="public-registration-documents">
        <legend>Documentos requeridos</legend>
        <p>Adjunta archivos de máximo {MAX_DOCUMENT_SIZE_LABEL}. La foto será usada posteriormente para emitir la credencial.</p>
        <div className="public-registration-form-grid">
          <Field label="Foto de medio cuerpo (JPG, PNG o WebP)">
            <FileUploadControl
              key={`photo-${fileInputKey}`}
              id={`public-registration-photo-${fileInputKey}`}
              accept={documentAccept.photo}
              file={form.photo_file}
              onChange={(file) => updateFile("photo_file", file)}
            />
          </Field>
        </div>
      </fieldset>
    ) : null;
  }

  // Pago de una orden YA creada (comprobante pendiente, en revisión o Mercado
  // Pago pendiente). Se usa en el paso 4 y también con inscripciones cerradas:
  // pagar lo que ya se debe no depende de que el registro siga abierto
  // (Adela 2026-10-23).
  function renderExistingOrderPayment() {
    return (
      <>
        {receiptUnderReview && !pendingNeedsAction(pendingRegistration) ? (
          <div className="public-registration-payment-box public-registration-pending-payment-box">
            <span>Comprobante recibido</span>
            <strong>El organizador lo está revisando.</strong>
            <p>
              Cuando lo valide, tu inscripción quedará como pagada. Si subiste un archivo equivocado,
              reemplázalo aquí; el organizador verá solo el más reciente.
            </p>
            <OrderBankDetails info={reviewRegistration?.order?.payment_instructions} />
            {reviewRegistration?.payment_receipt_url ? (
              <a className="button button--ghost" href={reviewRegistration.payment_receipt_url} target="_blank" rel="noreferrer">
                Ver comprobante actual
              </a>
            ) : null}
            <Field label="Nuevo comprobante de pago (PDF o imagen)">
              <FileUploadControl
                key={`review-receipt-${fileInputKey}`}
                id={`public-registration-review-receipt-${fileInputKey}`}
                accept={documentAccept.receipt}
                file={pendingReceiptFile}
                onChange={setPendingReceiptFile}
              />
            </Field>
            <button
              className="button button--primary"
              type="button"
              onClick={replacePaymentReceipt}
              disabled={uploadingPendingReceipt || !pendingReceiptFile}
            >
              {uploadingPendingReceipt ? "Subiendo…" : "Reemplazar comprobante"}
            </button>
          </div>
        ) : null}
        {pendingNeedsAction(pendingRegistration) && usesOnlinePayment ? (
          <div className="public-registration-payment-box public-registration-pending-payment-box">
            <span>Preinscripción guardada</span>
            <strong>Tu avance no se perdió.</strong>
            <p>
              Ya tenemos tus datos de jugador
              {pendingRegistration.order ? ` y tu orden #${pendingRegistration.order.id} está pendiente de pago` : ""}. Paga en línea con
              Mercado Pago para que el organizador confirme tu inscripción.
            </p>
            {pendingRegistration.order?.public_token ? (
              <button
                className="button button--primary"
                type="button"
                onClick={() => void openCheckoutFor(pendingRegistration.order)}
                disabled={checkoutBusy}
              >
                {checkoutBusy ? "Abriendo Mercado Pago…" : "Pagar en línea con Mercado Pago"}
              </button>
            ) : null}
            {checkoutError ? <div className="alert alert--error" role="alert">{checkoutError}</div> : null}
          </div>
        ) : pendingRegistration?.needs_payment_receipt ? (
          <div className="public-registration-payment-box public-registration-pending-payment-box">
            <span>Preinscripción guardada</span>
            <strong>Tu avance no se perdió.</strong>
            <p>
              Ya tenemos tus datos de jugador. Sube aquí el comprobante de pago para que el organizador pueda revisarlo y confirmar tu inscripción.
            </p>
            <OrderBankDetails info={pendingRegistration.order?.payment_instructions} />
            <Field label="Comprobante de pago pendiente (PDF o imagen)">
              <FileUploadControl
                key={`pending-receipt-${fileInputKey}`}
                id={`public-registration-pending-receipt-${fileInputKey}`}
                accept={documentAccept.receipt}
                file={pendingReceiptFile}
                onChange={setPendingReceiptFile}
              />
            </Field>
            <button
              className="button button--primary"
              type="button"
              onClick={uploadPendingPaymentReceipt}
              disabled={uploadingPendingReceipt}
            >
              {uploadingPendingReceipt ? "Subiendo…" : "Subir comprobante y terminar"}
            </button>
          </div>
        ) : null}
      </>
    );
  }

  // A quién se le está pagando (cuenta familiar): el paso de pago lo dice
  // para que la mamá vea que abrió la orden del hijo correcto.
  const payingName =
    form.player_profile_id && (pendingNeedsAction(pendingRegistration) || receiptUnderReview)
      ? familyProfiles.find((profile) => profile.id === form.player_profile_id)?.display_name?.trim() || null
      : null;

  if (loading) {
    return <section className="public-registration-page"><div className="card"><p>Cargando convocatoria…</p></div></section>;
  }

  if (!payload) {
    return <section className="public-registration-page"><div className="alert alert--error">{error ?? "Torneo no encontrado."}</div></section>;
  }

  // Inscripciones cerradas pero con una orden YA creada por pagar o en
  // revisión: la familia todavía puede subir su comprobante (el backend lo
  // permite; antes esta pantalla escondía el paso de pago).
  const closedWithOrderToPay =
    !registrationOpen && isAuthenticatedPlayer && (pendingNeedsAction(pendingRegistration) || (receiptUnderReview && paymentDeepLink));
  if (!registrationOpen && !submittedName) {
    return (
      <section className="public-registration-page">
        <PublicAdBannerSlot position="superior" banners={topSponsors} tournamentName={payload.tournament.name} />
        <article className="card public-registration-success-card">
          <div className="public-registration-hero__brand-frame">
            <img className="public-registration-hero__logo public-registration-hero__logo--torneos" src={TORNEOS123_DARK_LOGO_SRC} alt="Torneos123" />
            {organizerLogoUrl ? (
              <img className="public-registration-hero__logo public-registration-hero__logo--organizer" src={organizerLogoUrl} alt={payload.tournament.name} />
            ) : null}
          </div>
          <p className="eyebrow">Inscripciones cerradas</p>
          <h1>{payload.tournament.name}</h1>
          <p>
            {payload.closed_reason === "full"
              ? "Cupo lleno: todas las categorías alcanzaron su límite de inscripciones. Contacta al organizador si tienes dudas."
              : payload.closed_reason === "organizer"
                ? "El organizador cerró las inscripciones. Contacta al organizador si tienes dudas."
              : payload.closed_reason === "closed"
                ? "El torneo terminó y ya no recibe inscripciones. Contacta al organizador si tienes dudas."
                : payload.closed_reason === "deadline"
                  ? "Ya pasó la fecha de cierre de inscripciones. Contacta al organizador si tienes dudas."
                : "El torneo ya comenzó, por lo que el registro en línea está cerrado. Contacta al organizador si tienes dudas."}
          </p>
        </article>
        {paymentDeepLink && authStatus === "anonymous" ? (
          <article className="card public-registration-payment-step" aria-label="Terminar tu pago">
            <div className="public-registration-payment-box public-registration-pending-payment-box">
              <span>Pago pendiente</span>
              <strong>Inicia sesión para terminar tu pago.</strong>
              <p>
                Ya no se reciben inscripciones nuevas, pero si ya tienes una inscripción por pagar puedes subir aquí tu
                comprobante. Entra con tu cuenta de jugador.
              </p>
              <Link className="button button--primary" to={loginPath}>Inicia sesión para terminar tu pago</Link>
            </div>
          </article>
        ) : null}
        {paidNotice && !closedWithOrderToPay ? (
          <article className="card public-registration-payment-step" aria-label="Estado de tu pago">
            <div className="alert alert--success">{paidNotice}</div>
          </article>
        ) : null}
        {closedWithOrderToPay ? (
          <article className="card public-registration-payment-step" aria-label="Pago de tu inscripción">
            <div className="public-section-heading">
              <span>04</span>
              <div>
                <h2>Pago{payingName ? ` de ${payingName}` : ""}</h2>
                <p>Ya no se reciben inscripciones nuevas, pero aún puedes terminar el pago de esta inscripción.</p>
              </div>
            </div>
            {error ? <div className="alert alert--error">{error}</div> : null}
            {message ? <div className="alert alert--success">{message}</div> : null}
            {renderExistingOrderPayment()}
          </article>
        ) : null}
        <PublicAdBannerSlot position="inferior" banners={bottomSponsors} tournamentName={payload.tournament.name} />
      </section>
    );
  }

  if (submittedName) {
    // El grupo de WhatsApp ya no viaja en el link público: el backend lo
    // entrega en la respuesta de la inscripción solo si quedó pagada o sin
    // costo.
    const whatsappGroupLink = submittedRegistration?.whatsapp_group_link?.trim() || null;
    // Varios grupos con su nombre (Eduardo 2026-10-01), misma regla.
    const whatsappGroups = submittedWhatsappGroups(submittedRegistration);
    const canEnterWhatsappGroup = whatsappGroups.length > 0;
    const hasWhatsappGroup = Boolean(payload.tournament.has_whatsapp_group || whatsappGroupLink);
    const submittedOrder = submittedRegistration?.order ?? null;
    const orderPaid = submittedOrder?.status === "paid";
    const canPayOnline = Boolean(usesOnlinePayment && submittedOrder && !orderPaid && submittedOrder.public_token);
    const paymentPending = Boolean(submittedOrder && !orderPaid);
    const deadlineLabel = payload.tournament.registration_deadline
      ? formatDate(payload.tournament.registration_deadline, "")
      : "";
    const categoriesLine = submittedRegistration?.divisions?.length
      ? `${submittedRegistration.divisions.map((division) => division.name).join(" · ")} · ${payload.tournament.name}`
      : payload.tournament.name;
    const nextSteps: string[] = [];
    if (paymentPending) nextSteps.push("Paga la inscripción. Te avisamos cuando se confirme.");
    if (hasWhatsappGroup && !canEnterWhatsappGroup) {
      nextSteps.push("Recibes el grupo de WhatsApp del torneo en cuanto se confirme tu pago.");
    }
    nextSteps.push(
      isAuthenticatedPlayer
        ? "El organizador aprueba tu inscripción y la verás en tu cuenta."
        : "El organizador aprueba tu inscripción y te llega un correo de bienvenida con el acceso a tu cuenta.",
    );
    const paymentAndGroups = (
      <>
        {submittedOrder ? (
          canPayOnline ? (
            <div className="public-registration-payment-summary public-registration-payment-summary--due">
              <span>Falta un paso: pagar</span>
              <small>Orden #{submittedOrder.id}</small>
              <strong className="public-registration-payment-summary__amount">
                {formatMoney(submittedOrder.gross_amount, submittedOrder.currency)}
              </strong>
              <button
                className="button public-registration-payment-summary__pay"
                type="button"
                onClick={() => void openSubmittedCheckout()}
                disabled={checkoutBusy}
              >
                {checkoutBusy ? "Abriendo Mercado Pago…" : "Pagar ahora con Mercado Pago"}
              </button>
              {checkoutError ? <div className="alert alert--error" role="alert">{checkoutError}</div> : null}
              <p>
                ¿No puedes pagar ahora? Te mandamos este enlace por correo. Paga antes del{" "}
                {deadlineLabel || "cierre de inscripciones"} o podrías perder tu lugar.
              </p>
            </div>
          ) : (
            <div className="public-registration-payment-summary">
              <span>{orderPaid ? "Pago confirmado" : "Pago pendiente de confirmación"}</span>
              <small>Orden #{submittedOrder.id}</small>
              <strong className="public-registration-payment-summary__amount">
                {formatMoney(submittedOrder.gross_amount, submittedOrder.currency)}
              </strong>
              {submittedRegistration?.payment.checkout_message ? <p>{submittedRegistration.payment.checkout_message}</p> : null}
              {checkoutError ? <div className="alert alert--error" role="alert">{checkoutError}</div> : null}
            </div>
          )
        ) : null}
        {canEnterWhatsappGroup ? (
          <div className="public-registration-whatsapp-list">
            {whatsappGroups.map((group, index) => (
              <a key={`${group.url}-${index}`} className="public-registration-whatsapp-button" href={group.url} target="_blank" rel="noreferrer">
                <WhatsAppIcon />
                <span>{whatsappGroupButtonLabel(group, index, whatsappGroups.length)}</span>
              </a>
            ))}
          </div>
        ) : null}
        <div className="public-registration-done__next">
          <p className="public-registration-done__next-title">Qué sigue</p>
          <ol>
            {nextSteps.map((step) => (
              <li key={step}>{step}</li>
            ))}
          </ol>
        </div>
      </>
    );
    return (
      <section className="public-registration-page">
        <PublicAdBannerSlot position="superior" banners={topSponsors} tournamentName={payload.tournament.name} />
        <article className="card public-registration-success-card">
          <div className="public-registration-hero__brand-frame">
            <img className="public-registration-hero__logo public-registration-hero__logo--torneos" src={TORNEOS123_DARK_LOGO_SRC} alt="Torneos123" />
            {organizerLogoUrl ? (
              <img className="public-registration-hero__logo public-registration-hero__logo--organizer" src={organizerLogoUrl} alt={payload.tournament.name} />
            ) : null}
          </div>
          <FamilyDoneCard
            playerName={submittedName}
            categoriesLine={categoriesLine}
            familyEmail={submittedWhoPlays === "child" ? submittedAccountEmail : undefined}
            accountPath={isAuthenticatedPlayer ? "/player" : null}
            onRegisterAnother={submittedWhoPlays === "child" ? registerAnotherPlayer : undefined}
          >
            {paymentAndGroups}
          </FamilyDoneCard>
        </article>
        <PublicAdBannerSlot position="inferior" banners={bottomSponsors} tournamentName={payload.tournament.name} />
      </section>
    );
  }

  return (
    <section className="public-registration-page">
      <PublicAdBannerSlot position="superior" banners={topSponsors} tournamentName={payload.tournament.name} />
      <header className={`public-registration-hero public-registration-hero--pro${tournamentHeroImageUrl ? " has-visual" : ""}`}>
        <div className="public-registration-hero__court" aria-hidden="true" />
        {!tournamentHeroImageUrl ? (
          <div className="public-registration-hero__visual public-registration-hero__visual--abstract" aria-hidden="true">
            <span className="public-registration-hero__ball" />
          </div>
        ) : null}
        <div className="public-registration-hero__content">
          <div className="public-registration-hero__brands">
            <img className="public-registration-hero__brand-torneos" src={TORNEOS123_DARK_LOGO_SRC} alt="Torneos123" />
            {organizerLogoUrl ? (
              <>
                <span className="public-registration-hero__brands-divider" />
                <span className="public-registration-hero__brand-organizer">
                  <img src={organizerLogoUrl} alt={heroOrganizerName ?? payload.tournament.name} />
                </span>
              </>
            ) : null}
          </div>
          <p className="public-registration-hero__eyebrow">Torneos123 · Inscripción oficial</p>
          <h1 className="public-registration-hero__title">
            <span className="public-registration-hero__title-main">{heroTitle.main}</span>
            {heroTitle.accent ? <span className="public-registration-hero__title-accent">{heroTitle.accent}</span> : null}
          </h1>
          <p className="public-registration-hero__lead">Lee la convocatoria y completa tu alta de jugador para solicitar inscripción a este torneo.</p>
          {heroOrganizerName || heroMeta.length > 0 ? (
            <div className="public-registration-hero__meta">
              {heroOrganizerName ? <span className="public-registration-hero__badge">Torneo · {heroOrganizerName}</span> : null}
              {heroMeta.map((item) => (
                <span key={item} className="public-registration-hero__meta-item">{item}</span>
              ))}
            </div>
          ) : null}
        </div>
        {tournamentHeroImageUrl ? (
          <figure className="public-registration-hero__visual">
            {/* Póster COMPLETO del organizador (Eduardo 2026-09-10): lo que
                sube cada organizador se ve bien y entero, nunca recortado.
                Clic → en grande + descargar (Eduardo 2026-09-15). */}
            <button
              type="button"
              className="public-registration-hero__poster-button"
              onClick={() => setPosterOpen(true)}
              aria-label="Ver el póster en grande"
              title="Ver en grande"
            >
              <img src={tournamentHeroImageUrl} alt={`Póster de ${payload.tournament.name}`} />
              <span className="public-registration-hero__poster-hint" aria-hidden="true">🔍 Ver en grande</span>
            </button>
          </figure>
        ) : null}
        {tournamentHeroImageUrl && posterOpen
          ? createPortal(
            <div className="poster-lightbox" role="dialog" aria-modal="true" aria-label={`Póster de ${payload.tournament.name}`}>
              <div className="poster-lightbox__backdrop" onClick={() => setPosterOpen(false)} />
              <div className="poster-lightbox__panel">
                <img src={tournamentHeroImageUrl} alt={`Póster de ${payload.tournament.name}`} />
                <div className="poster-lightbox__actions">
                  <a className="button button--primary" href={tournamentHeroImageUrl} download target="_blank" rel="noreferrer">
                    ⬇ Descargar póster
                  </a>
                  <button type="button" className="button button--secondary" onClick={() => setPosterOpen(false)}>
                    Cerrar
                  </button>
                </div>
              </div>
              <button type="button" className="poster-lightbox__close" onClick={() => setPosterOpen(false)} aria-label="Cerrar">×</button>
            </div>,
            document.body,
          )
          : null}
        <span className="public-registration-hero__tagline" aria-hidden="true">Compite · Progresa · Gana</span>
      </header>

      {payload.extemporaneous ? (
        <div className="alert alert--info" role="note">
          <strong>Inscripción extemporánea:</strong> la fecha límite ya pasó, pero el organizador sigue aceptando inscripciones hasta el inicio del torneo.
        </div>
      ) : null}

      {activeStep < 3 && error ? <div className="alert alert--error">{error}</div> : null}
      {/* Link de pago: el aviso (pago recibido, sin orden, sin pago pendiente)
          queda arriba y fijo, no solo en el aviso flotante. */}
      {(activeStep < 3 || (paymentDeepLink && activeStep === 3)) && message ? <div className="alert alert--success">{message}</div> : null}

      <div className="public-registration-shell" ref={wizardRef}>
        <nav className="public-registration-stepper" aria-label="Pasos de inscripción">
          {[
            [1, "Convocatoria"],
            [2, "Reglamento"],
            [3, "Alta de jugador"],
            [4, "Pago"],
          ].map(([step, label]) => (
            <button
              key={step}
              type="button"
              className={`public-registration-stepper__item${activeStep === step ? " is-active" : ""}${Number(step) <= maxStepVisited ? " is-available" : ""}`}
              onClick={() => handleStepClick(Number(step))}
              aria-current={activeStep === step ? "step" : undefined}
            >
              <span>{step}</span>
              {label}
            </button>
          ))}
        </nav>

        <div className="public-registration-grid public-registration-wizard">
          <article className="card public-convocatoria-card public-registration-step-card" hidden={activeStep !== 1}>
            <div className="public-section-heading">
              <span>01</span>
              <div>
                <h2>Convocatoria</h2>
                <p>Información publicada por el organizador.</p>
              </div>
            </div>
            <div className="public-convocatoria-facts">
              {facts.map(([label, value]) => (
                <div key={label}>
                  <span>{label}</span>
                  <strong>{value}</strong>
                </div>
              ))}
            </div>
            {payload.tournament.call_pdf_url ? (
              <a
                className="button button--secondary"
                href={resolvePublicAssetUrl(payload.tournament.call_pdf_url) ?? undefined}
                target="_blank"
                rel="noreferrer"
              >
                Descargar convocatoria oficial (PDF)
              </a>
            ) : null}
            <div className="public-convocatoria-body">
              {convocatoriaLabels.map(([key, label]) => (
                <section key={key}>
                  <h3>{label}</h3>
                  <p>{valueOrDash(convocatoria[key]).split("\n").map((line, index) => <span key={`${key}-${index}`}>{line}<br /></span>)}</p>
                </section>
              ))}
            </div>
            <div className="public-registration-step-actions">
              <button className="button button--primary" type="button" onClick={() => openStep(2)}>
                Continuar a reglamento
              </button>
            </div>
          </article>

          <article className="card public-reglamento-card public-registration-step-card" hidden={activeStep !== 2}>
            <div className="public-section-heading">
              <span>02</span>
              <div>
                <h2>Reglamento</h2>
                <p>Lee las reglas del torneo antes de completar tu alta.</p>
              </div>
            </div>
            {ruleAttachments(payload.rule).map((item) => (
              <a
                key={item.url}
                className="button button--secondary public-registration-rule-download"
                href={item.url}
                target="_blank"
                rel="noreferrer"
                download
              >
                Descargar PDF{item.filename ? ` · ${item.filename}` : " original"}
              </a>
            ))}
            <div className="public-convocatoria-body">
              {rulesLabels.map(([key, label]) => (
                <section key={key}>
                  <h3>{label}</h3>
                  <p>{valueOrDash(convocatoria[key]).split("\n").map((line, index) => <span key={`${key}-${index}`}>{line}<br /></span>)}</p>
                </section>
              ))}
            </div>
            <label className="form-checkbox public-registration-consent public-registration-rules-acceptance">
              <input type="checkbox" checked={rulesAccepted} onChange={(e) => { setRulesAccepted(e.target.checked); setError(null); }} />
              <span>Estoy de acuerdo con el reglamento del torneo.</span>
            </label>
            <div className="public-registration-step-actions">
              <button className="button button--secondary" type="button" onClick={() => openStep(1)}>
                Volver a convocatoria
              </button>
              <button className="button button--primary" type="button" onClick={continueFromRules}>
                Continuar a alta de jugador
              </button>
            </div>
          </article>

          <form ref={submitFormRef} className="card public-registration-form public-registration-step-card" noValidate onSubmit={handleSubmit} hidden={activeStep !== 3 && activeStep !== 4}>
            <div hidden={activeStep !== 3}>
              <div className="public-section-heading">
                <span>03</span>
                <div>
                  <h2>Alta de jugador</h2>
                  {/* Eduardo 2026-09-10: sin la frase de CURP/ID Federativo/WTN —
                      no todos los organizadores piden esos campos. */}
                  <p>Completa tus datos para solicitar tu inscripción.</p>
                </div>
              </div>

              {/* Eduardo 2026-10-01: sin los párrafos de «Importante»; la
                  pregunta «¿Quién va a jugar?» y las dos cajas lo explican.
                  Sin sesión, el acceso queda como una liga chica. */}
              {isAuthenticatedPlayer || isAuthenticatedNonPlayer ? (
                <div className="public-registration-login-card">
                  <div>
                    <strong>{isAuthenticatedPlayer ? "Cuenta familiar activa" : "Sesión de administración abierta"}</strong>
                    <p>
                      {profileAutofillStatus === "loaded"
                        ? "Elige un perfil familiar existente o agrega un nuevo jugador a tu cuenta antes de enviar la inscripción."
                        : isAuthenticatedNonPlayer
                          ? "Tienes abierta una sesión de administración. Cambia a una cuenta familiar para inscribir jugadores desde el formulario público."
                          : "Cargando tu cuenta familiar."}
                    </p>
                  </div>
                  {isAuthenticatedPlayer ? (
                    <span className="public-registration-login-card__badge">Sesión iniciada</span>
                  ) : (
                    <button className="button button--secondary" type="button" onClick={goToPlayerLogin}>
                      Cambiar a cuenta familiar
                    </button>
                  )}
                </div>
              ) : (
                <p className="public-registration-login-link">
                  ¿Ya tiene cuenta?{" "}
                  <button className="link-button public-registration-login-link__button" type="button" onClick={goToPlayerLogin}>
                    Inicia sesión
                  </button>
                </p>
              )}

              {profileAutofillStatus === "loading" ? <p className="public-registration-parser-status">Cargando tus datos guardados…</p> : null}
              {profileAutofillStatus === "missing" ? <p className="public-registration-parser-status">Tu cuenta todavía no tiene perfiles de jugador; agrega el primer perfil familiar aquí mismo.</p> : null}
              {profileAutofillStatus === "error" ? <p className="public-registration-parser-status">No pudimos cargar tus perfiles guardados. Intenta de nuevo o contacta al organizador.</p> : null}

              {askWhoPlays ? <WhoPlaysChoice value={whoPlays} onChange={changeWhoPlays} /> : null}

              {childMode ? (
                <>
                  <GuardianBox
                    values={{
                      guardian_name: form.guardian_name,
                      guardian_phone: form.guardian_phone,
                      guardian_email: form.guardian_email,
                      guardian_relationship: form.guardian_relationship,
                    }}
                    onChange={updateGuardian}
                    showPhone={guardianBox.phone}
                    showRelationship={guardianBox.relationship}
                    emailReadOnly={isAuthenticatedPlayer && Boolean(sessionAccount?.email)}
                  >
                    {customFieldsBySection.guardian.map(renderCustomField)}
                  </GuardianBox>
                  <section className="public-registration-box public-registration-box--player" aria-labelledby="public-registration-player-box-title">
                    <h3 className="public-registration-box__title" id="public-registration-player-box-title">
                      <span className="public-registration-box__number" aria-hidden="true">2</span>
                      <span><span className="sr-only">2 · </span>Datos de quien va a jugar</span>
                    </h3>
                    {renderFamilyPicker()}
                    {renderCurpStep()}
                    {renderChildPlayerFields()}
                  </section>
                  {renderFeeTypeSection()}
                  {renderCategoriesSection()}
                  {renderOtherCustomSection()}
                  {renderCoachFieldset()}
                  {renderPhotoFieldset()}
                </>
              ) : (
                <>
                  {renderFamilyPicker()}
                  {renderCurpStep()}
                  {renderFeeTypeSection()}
                  {renderCategoriesSection()}
                  {renderSelfPlayerFields()}
                  {renderTutorLegalFieldset()}
                  {renderCoachFieldset()}
                  {renderPhotoFieldset()}
                </>
              )}

              {confirmOpen ? (
                <RegistrationConfirmCard
                  summary={confirmText}
                  onConfirm={confirmPlayerData}
                  onCorrect={correctPlayerData}
                />
              ) : (
                <div className="public-registration-step-actions">
                  <button className="button button--secondary" type="button" onClick={() => openStep(2)}>
                    Volver a reglamento
                  </button>
                  <button ref={saveDataButtonRef} className="button button--primary public-registration-save-data" type="button" onClick={savePlayerDataAndContinue}>
                    Guardar datos
                  </button>
                </div>
              )}
            </div>

            <div className="public-registration-payment-step" hidden={activeStep !== 4}>
              <div className="public-section-heading">
                <span>04</span>
                <div>
                  <h2>Pago{payingName ? ` de ${payingName}` : ""}</h2>
                  <p>{usesOnlinePayment ? "Este torneo tiene pago en línea disponible." : "Adjunta tu comprobante para que el organizador valide tu inscripción."}</p>
                </div>
              </div>
              {paymentPreview ? (
                <div className="alert alert--info">
                  Vista previa de pago: puedes revisar esta pantalla sin enviar una inscripción.
                </div>
              ) : null}
              {paymentDeepLink && authStatus === "anonymous" ? (
                <div className="public-registration-payment-box public-registration-pending-payment-box">
                  <span>Comprobante de pago</span>
                  <strong>Inicia sesión para subir tu comprobante.</strong>
                  <p>
                    Entra con tu cuenta de jugador (la del correo de bienvenida) y aquí mismo podrás adjuntar
                    tu comprobante de transferencia.
                  </p>
                  <Link className="button button--primary" to={loginPath}>Iniciar sesión</Link>
                </div>
              ) : null}
              {renderExistingOrderPayment()}
              {usesOnlinePayment ? (
                pendingNeedsAction(pendingRegistration) ? null : (
                <div className="public-registration-payment-box public-registration-online-checkout-box">
                  <span>Pago en línea disponible</span>
                  <p>
                    Guardaremos tu inscripción con orden pendiente. Al enviar tu inscripción te daremos el link para pagar en línea con Mercado Pago.
                  </p>
                </div>
                )
              ) : pendingNeedsAction(pendingRegistration) ? null : (
                <>
                  <div className="alert alert--info">
                    <strong>Cobro por fuera:</strong> sube tu comprobante de pago para que el organizador lo revise manualmente.
                  </div>
                  {/* Cuentas de cobro F1: con una orden YA creada solo se muestra la
                      cuenta congelada de esa orden; la cuenta actual del torneo
                      es solo para quien aún no tiene orden. */}
                  {existingOrder ? (
                    receiptUnderReview ? null : <OrderBankDetails info={existingOrder.payment_instructions} />
                  ) : registrationSettings?.collection_bank_account ? (
                    <div className="public-registration-payment-box public-registration-bank-details">
                      <span>Cuenta de cobro</span>
                      <strong>{registrationSettings.collection_bank_account.bank_name}</strong>
                      <p>Beneficiario: {registrationSettings.collection_bank_account.beneficiary_name}</p>
                      {registrationSettings.collection_bank_account.account_number ? <p>Cuenta: {registrationSettings.collection_bank_account.account_number}</p> : null}
                      {registrationSettings.collection_bank_account.clabe ? <p>CLABE: {registrationSettings.collection_bank_account.clabe}</p> : null}
                      {registrationSettings.collection_bank_account.payment_concept ? <p><strong>Concepto:</strong> {registrationSettings.collection_bank_account.payment_concept}</p> : null}
                    </div>
                  ) : null}
                  <fieldset className="public-registration-documents public-registration-payment-box">
                    <legend>Comprobante de pago</legend>
                    <div className="public-registration-form-grid">
                      <Field label="Comprobante de pago de inscripción (PDF o imagen)">
                        <FileUploadControl
                          key={`receipt-${fileInputKey}`}
                          id={`public-registration-receipt-${fileInputKey}`}
                          accept={documentAccept.receipt}
                          file={form.payment_receipt_file}
                          onChange={(file) => updateFile("payment_receipt_file", file)}
                        />
                      </Field>
                    </div>
                  </fieldset>
                </>
              )}

              <div className="public-registration-consents">
                <label className="form-checkbox public-registration-consent">
                  <input type="checkbox" checked={form.privacy_notice_accepted} onChange={(e) => update("privacy_notice_accepted", e.target.checked)} />
                  <span>1. Acepto Aviso de Privacidad.</span>
                </label>
                <label className="form-checkbox public-registration-consent">
                  <input type="checkbox" checked={form.data_consent} onChange={(e) => update("data_consent", e.target.checked)} />
                  <span>2. Acepto el uso de mis datos para gestionar mi inscripción, comunicación del torneo y operación de Torneos123.</span>
                </label>
              </div>

              {form.first_name.trim() && !pendingNeedsAction(pendingRegistration) ? (
                <p className="public-registration-confirm-line">{confirmText}</p>
              ) : null}
              <div className="public-registration-step-actions">
                <button className="button button--secondary" type="button" onClick={() => openStep(3)}>
                  Volver a alta
                </button>
                {pendingNeedsAction(pendingRegistration) ? null : (
                  <button className="button button--primary" type="submit" disabled={submitting}>
                    {submitting
                      ? "Enviando…"
                      : usesOnlinePayment
                        ? "Guardar y Confirmar Inscripción"
                        : form.payment_receipt_file
                          ? "Enviar inscripción"
                          : "Guardar y subir comprobante después"}
                  </button>
                )}
              </div>
            </div>

            <div
              ref={feedbackRef}
              tabIndex={-1}
              className="public-registration-submit-feedback"
              aria-live="polite"
            >
              {/* Con folio y «Reintentar» a la vista no se repite en aviso
                  flotante: en celular tapaba el botón (QA 2026-10-23). */}
              {error ? (
                <div className="alert alert--error" data-no-toast={submitFailure ? "" : undefined}>
                  {error}
                </div>
              ) : null}
              {submitFailure && error ? (
                <div className="public-registration-retry">
                  <p>
                    Antes de volver a enviarla revisamos si sí quedó guardada, para que no se duplique.
                    {token ? null : " Si ya tienes cuenta, inicia sesión y revisa tus inscripciones."}
                  </p>
                  <button
                    type="button"
                    className="button button--primary"
                    disabled={checkingSaved || submitting}
                    onClick={() => void retryAfterFailure()}
                  >
                    {checkingSaved ? "Revisando…" : "Reintentar"}
                  </button>
                </div>
              ) : null}
              {showAccountRecovery ? (
                <div className="public-registration-recovery-card">
                  {recoveryDetails?.has_account && recoveryDetails.can_email_reset && recoveryEmailIsAccount ? (
                    <>
                      <strong>
                        {recoveryDetails.never_logged_in
                          ? "Ya tienes cuenta en Torneos123 y todavía no la has usado."
                          : "Ya tienes cuenta en Torneos123."}
                      </strong>
                      <p>
                        {recoveryDetails.never_logged_in
                          ? `Te mandamos un correo a ${recoveryDetails.masked_email ?? "tu correo"} para crear tu contraseña. Después regresas a este link, inicias sesión y tu inscripción tarda un minuto.`
                          : `Inicia sesión para inscribirte. Si no recuerdas tu contraseña, te mandamos un correo a ${recoveryDetails.masked_email ?? "tu correo"} para crear una nueva.`}
                      </p>
                      {recoveryEmailState === "sent" ? (
                        <div className="alert alert--success" role="status">
                          Listo. Si no te llega en unos minutos, revisa spam o promociones. Después crea tu contraseña y vuelve a esta página.
                        </div>
                      ) : null}
                      {recoveryEmailState === "error" ? (
                        <div className="alert alert--error" role="alert">No pudimos enviar el correo. Intenta de nuevo o usa "Recuperar contraseña".</div>
                      ) : null}
                      <div className="public-registration-recovery-card__actions">
                        {recoveryEmailState !== "sent" ? (
                          <button className="button button--primary" type="button" onClick={() => void sendRecoveryEmail()} disabled={recoveryEmailState === "sending"}>
                            {recoveryEmailState === "sending" ? "Enviando…" : recoveryDetails.never_logged_in ? "Enviarme el correo para crear mi contraseña" : "Enviarme el correo para cambiar mi contraseña"}
                          </button>
                        ) : null}
                        <button className="button button--secondary" type="button" onClick={goToPlayerLogin}>{isAuthenticatedNonPlayer ? "Cambiar a cuenta familiar" : "Iniciar sesión"}</button>
                        <Link className="button button--ghost" to={recoveryUserPath}>Recuperar usuario</Link>
                      </div>
                    </>
                  ) : recoveryDetails?.has_account && recoveryDetails.can_email_reset ? (
                    <>
                      <strong>Ya tienes cuenta en Torneos123, con otro correo.</strong>
                      <p>
                        {`Tu cuenta usa el correo ${recoveryDetails.masked_email ?? "con el que te diste de alta"}. Escríbelo en «Recuperar contraseña» y te mandamos el enlace. Si no reconoces ese correo, pídele tu PIN de acceso al organizador.`}
                      </p>
                      <div className="public-registration-recovery-card__actions">
                        <Link className="button button--primary" to={recoveryPasswordPath}>Recuperar contraseña</Link>
                        <button className="button button--secondary" type="button" onClick={goToPlayerLogin}>{isAuthenticatedNonPlayer ? "Cambiar a cuenta familiar" : "Iniciar sesión"}</button>
                        <Link className="button button--ghost" to={recoveryUserPath}>Recuperar usuario</Link>
                      </div>
                    </>
                  ) : (
                    <>
                      <strong>{recoveryDetails?.has_account === false ? "Tus datos ya están en el catálogo del club." : "¿Ya tienes cuenta?"}</strong>
                      <p>
                        {recoveryDetails?.has_account === false
                          ? "El organizador ya te tiene registrado pero tu cuenta aún no tiene acceso: pídele que te envíe la bienvenida o un PIN, y luego vuelve a este link para inscribirte."
                          : "Usa tu usuario existente. Si no lo recuerdas o perdiste tu PIN/contraseña, recupera el acceso antes de volver a intentar la inscripción."}
                      </p>
                      <div className="public-registration-recovery-card__actions">
                        <button className="button button--secondary" type="button" onClick={goToPlayerLogin}>{isAuthenticatedNonPlayer ? "Cambiar a cuenta familiar" : "Iniciar sesión"}</button>
                        <Link className="button button--ghost" to={recoveryUserPath}>Recuperar usuario</Link>
                        <Link className="button button--ghost" to={recoveryPasswordPath}>Recuperar contraseña</Link>
                      </div>
                    </>
                  )}
                </div>
              ) : null}
              {message ? <div className="alert alert--success">{message}</div> : null}
            </div>
          </form>
        </div>
      </div>
      <PublicAdBannerSlot position="inferior" banners={bottomSponsors} tournamentName={payload.tournament.name} />
    </section>
  );
}
